import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { formatDateTime, formatDate, formatTime, parseServerDate, getDateInputValue, formatIST } from '../utils/dateUtils';
import { FiClock, FiX, FiPlus, FiEye, FiSearch, FiCalendar, FiLayers, FiChevronDown, FiCheck, FiAlertTriangle, FiEdit3, FiDownload } from 'react-icons/fi';
import { exportServiceRequestsPdf } from '../utils/pdfExportUtils';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { useSearchParams } from 'react-router-dom';
import {
  getAllServiceRequestActivities,
  getDefaultActivitiesForDepartment,
  normalizeDepartment,
  normalizeServiceRequestActivity,
  getBookingActivityGuestLabel
} from '../utils/departmentMapping';
import { renderFormattedText } from '../utils/formatUtils';

const DEPARTMENT_COLORS = {
  'Housekeeping Service': 'blue',
  'Maintenance Assistance': 'orange',
  'IT Support': 'purple',
  'Front Office': 'gray',
  'SPA Appointment': 'pink',
  'Yoga & Wellness': 'green',
  'Salon': 'pink'
};

/** Dashboard complete modal: button choices avoid native radio + fieldset hit-target quirks. */
const COMPLETION_STATUS_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no_dnd', label: 'No, room was on DND' },
  { value: 'no_critical', label: 'No, critical fix' }
];

const normalizeDigits = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits.length > 10 ? digits.slice(-10) : digits;
};

function ServiceRequests() {
  const { user } = useAuth();
  const canCancelRequests = Boolean(user?.can_cancel_service_requests || user?.role === 'admin');
  const allowedActivitiesKey = JSON.stringify(user?.allowed_activities || []);
  const allowedActivities = useMemo(() => {
    try {
      const parsed = JSON.parse(allowedActivitiesKey);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }, [allowedActivitiesKey]);
  const [page, setPage] = useState(1);
  const [totalRequests, setTotalRequests] = useState(0);
  const [limit] = useState(100);
  const [requests, setRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [requestView, setRequestView] = useState('pending');
  const [completedOutcomeFilter, setCompletedOutcomeFilter] = useState('all');
  const [dateFilterMode, setDateFilterMode] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [completingRequestId, setCompletingRequestId] = useState(null);
  const [cancellingRequest, setCancellingRequest] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [completionForm, setCompletionForm] = useState({
    completion_status: '',
    work_done_by: '',
    remarks: ''
  });
  const [completionSavingId, setCompletionSavingId] = useState(null);
  const [markingSeenId, setMarkingSeenId] = useState(null);
  const [detailsRequestId, setDetailsRequestId] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [showRoomWarningModal, setShowRoomWarningModal] = useState(false);
  const [disputeNoteModalRequest, setDisputeNoteModalRequest] = useState(null);
  const [disputeNoteText, setDisputeNoteText] = useState('');
  const [disputeNoteSubmitting, setDisputeNoteSubmitting] = useState(false);
  const [disputeNoteError, setDisputeNoteError] = useState('');
  const [manualSaving, setManualSaving] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [expandedSummaryOthers, setExpandedSummaryOthers] = useState({});
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryStatusFilter, setSummaryStatusFilter] = useState('all');
  const [summaryDateFilter, setSummaryDateFilter] = useState('all'); // 'all' | 'today' | 'custom'
  const [summaryDateFrom, setSummaryDateFrom] = useState('');
  const [summaryDateTo, setSummaryDateTo] = useState('');
  const [manualForm, setManualForm] = useState({
    guest_name: '',
    guest_room_no: '',
    guest_phone: '',
    activity: '',
    service_option_id: '',
    service_type: '',
    details: ''
  });
  const [departmentStaff, setDepartmentStaff] = useState([]);
  const [flowSettings, setFlowSettings] = useState(null);
  const [qrRooms, setQrRooms] = useState([]);

  useEffect(() => {
    api.get('/api/admin/staff')
      .then((res) => setDepartmentStaff(Array.isArray(res.data) ? res.data : []))
      .catch(() => setDepartmentStaff([]));

    api.get('/api/admin/whatsapp-flow-settings')
      .then((res) => setFlowSettings(res.data || {}))
      .catch(() => setFlowSettings({}));

    api.get('/api/admin/qr-rooms')
      .then((res) => setQrRooms(Array.isArray(res.data) ? res.data : []))
      .catch(() => setQrRooms([]));
  }, []);

  const { hiddenStaffPhones, hiddenStaffNames } = useMemo(() => {
    const phones = new Set();
    const names = new Set();
    (departmentStaff || []).forEach((s) => {
      if (s.is_hidden) {
        if (s.phone) phones.add(normalizeDigits(s.phone));
        if (s.name) names.add(s.name.trim().toLowerCase());
      }
    });
    return { hiddenStaffPhones: phones, hiddenStaffNames: names };
  }, [departmentStaff]);

  const checkRoomExistsInPresetsOrQr = useCallback((roomInput) => {
    const raw = String(roomInput || '').trim();
    if (!raw) return { isValid: false };
    const lower = raw.toLowerCase();
    const cleanRoom = lower.replace(/^(room|rm|#)\s*/i, '').trim();

    // 1. Check QR Rooms
    const allQrRooms = [
      ...(Array.isArray(qrRooms) ? qrRooms : []),
      ...(Array.isArray(flowSettings?.qr_rooms) ? flowSettings.qr_rooms : [])
    ];
    for (const r of allQrRooms) {
      const rStr = String(r || '').trim().toLowerCase();
      const rClean = rStr.replace(/^(room|rm|#)\s*/i, '').trim();
      if (rStr === lower || rClean === cleanRoom || rClean === lower || rStr === cleanRoom) {
        return { isValid: true, matchedSource: 'QR Rooms' };
      }
    }

    // 2. Check Quick Presets
    const presets = Array.isArray(flowSettings?.room_presets) ? flowSettings.room_presets : [];
    for (const preset of presets) {
      if (!preset) continue;
      const label = String(preset.label || '').trim();

      if (label && label.toLowerCase() === lower) {
        return { isValid: true, matchedSource: `Preset: ${label}` };
      }

      // Range preset
      if (preset.type === 'range' || (preset.start && preset.end)) {
        const startRaw = String(preset.start || '').trim();
        const endRaw = String(preset.end || '').trim();

        const inputNum = parseInt(cleanRoom.replace(/\D/g, ''), 10);
        const startNum = parseInt(startRaw.replace(/\D/g, ''), 10);
        const endNum = parseInt(endRaw.replace(/\D/g, ''), 10);

        if (!isNaN(inputNum) && !isNaN(startNum) && !isNaN(endNum)) {
          const inputLetters = cleanRoom.replace(/[^a-z]/gi, '').toLowerCase();
          const labelLetters = label.replace(/[^a-z]/gi, '').toLowerCase();
          const hasPrefixConflict = inputLetters && labelLetters && !labelLetters.includes(inputLetters) && !inputLetters.includes(labelLetters);

          if (!hasPrefixConflict && inputNum >= startNum && inputNum <= endNum) {
            return { isValid: true, matchedSource: `Preset: ${label || `${startRaw}-${endRaw}`}` };
          }
        }
      }

      // Custom preset (comma-separated rooms/tokens)
      const customStr = preset.custom || (preset.type !== 'range' ? preset.start : '');
      if (customStr) {
        const tokens = String(customStr).split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
        for (const token of tokens) {
          const tClean = token.replace(/^(room|rm|#)\s*/i, '').trim();
          if (token === lower || tClean === cleanRoom || token === cleanRoom || tClean === lower) {
            return { isValid: true, matchedSource: `Preset: ${label || 'Custom'}` };
          }
        }
      }
    }

    return { isValid: false };
  }, [qrRooms, flowSettings?.qr_rooms, flowSettings?.room_presets]);
  const isActivityBookingDepartmentUser = useMemo(() => {
    if (user?.role !== 'department') return false;
    const normalized = normalizeDepartment(user?.department || '');
    return ['SPA Appointment', 'Yoga & Wellness', 'Salon'].includes(normalized);
  }, [user?.department, user?.role]);

  const ALL_DEPARTMENTS = useMemo(() => {
    const canonical = getAllServiceRequestActivities().map((department) => ({
      id: department,
      name: department,
      color: DEPARTMENT_COLORS[department] || 'gray'
    }));

    return [{ id: 'all', name: 'All Requests', color: 'gray' }, ...canonical];
  }, []);

  const DEPARTMENTS = useMemo(() => {
    if (user?.role === 'admin' || user?.role === 'manager') {
      return ALL_DEPARTMENTS;
    }

    if (allowedActivities.length > 0) {
      const normalizedActivities = allowedActivities
        .map((activity) => normalizeServiceRequestActivity(activity))
        .filter(Boolean);

      return ALL_DEPARTMENTS.filter((department) =>
        department.id === 'all' || normalizedActivities.includes(department.id)
      );
    }

    if (user?.role === 'department' && user?.department) {
      const normalizedDepartment = normalizeDepartment(user.department);
      const departmentDefaults = getDefaultActivitiesForDepartment(normalizedDepartment);
      return ALL_DEPARTMENTS.filter((department) =>
        department.id === 'all' || departmentDefaults.includes(department.id)
      );
    }

    return ALL_DEPARTMENTS;
  }, [ALL_DEPARTMENTS, user?.department, user?.role, allowedActivities]);

  useEffect(() => {
    if (!DEPARTMENTS.some((department) => department.id === activeTab)) {
      setActiveTab('all');
    }
  }, [DEPARTMENTS, activeTab]);

  const fetchRequests = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      let url = `/api/roomservice/requests?page=${page}&limit=${limit}`;
      
      if (requestView !== 'summary' && requestView !== 'all') {
        url += `&status=${requestView}`;
      }
      if (requestView === 'completed' && completedOutcomeFilter && completedOutcomeFilter !== 'all') {
        url += `&outcome=${encodeURIComponent(completedOutcomeFilter)}`;
      }
      if (activeTab !== 'all') {
        url += `&category=${encodeURIComponent(activeTab)}`;
      }
      
      if (dateFilterMode === 'today') {
        const today = getDateInputValue(new Date());
        url += `&start_date=${today}&end_date=${today}`;
      } else if (dateFilterMode === 'last7') {
        const today = getDateInputValue(new Date());
        const last7Date = new Date();
        last7Date.setDate(last7Date.getDate() - 7);
        const last7 = getDateInputValue(last7Date);
        url += `&start_date=${last7}&end_date=${today}`;
      } else if (dateFilterMode === 'custom' && dateFrom && dateTo) {
        url += `&start_date=${dateFrom}&end_date=${dateTo}`;
      }
      
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }

      const response = await api.get(url);
      if (response.data && response.data.meta) {
        setRequests(response.data.data);
        setTotalRequests(response.data.meta.total);
      } else {
        setRequests(response.data);
      }
      setLastUpdatedAt(new Date().toISOString());
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [page, limit, requestView, completedOutcomeFilter, activeTab, dateFilterMode, dateFrom, dateTo, searchQuery]);

  const fetchSummary = useCallback(async () => {
    try {
      setLoadingSummary(true);
      let queryUrl = `/api/roomservice/summary?status=${summaryStatusFilter}`;
      if (summaryDateFilter === 'today') {
        const todayStr = getDateInputValue(new Date());
        queryUrl += `&start_date=${todayStr}&end_date=${todayStr}`;
      } else if (summaryDateFilter === 'custom' && summaryDateFrom && summaryDateTo) {
        queryUrl += `&start_date=${summaryDateFrom}&end_date=${summaryDateTo}`;
      }
      const response = await api.get(queryUrl);
      setSummaryData(response.data);
      setExpandedSummaryOthers({});
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setLoadingSummary(false);
    }
  }, [summaryStatusFilter, summaryDateFilter, summaryDateFrom, summaryDateTo]);

  const downloadSummaryCsv = () => {
    if (!summaryData || Object.keys(summaryData).length === 0) {
      alert('No summary data to export.');
      return;
    }

    const rows = [];
    Object.entries(summaryData).forEach(([dept, data]) => {
      const departmentTotal = Number(data.total || 0);
      rows.push([dept, 'Department Total', departmentTotal, '']);
      Object.entries(data.activities || {}).forEach(([activity, count]) => {
        rows.push([dept, activity, Number(count || 0), '']);
        if (String(activity).toLowerCase() === 'others') {
          Object.entries(data.others_breakdown || {}).forEach(([detail, detailCount]) => {
            rows.push([dept, 'Others > Detail', Number(detailCount || 0), detail]);
          });
        }
      });
      rows.push(['', '', '', '']);
    });

    const escapeCsv = (value) => {
      const str = String(value ?? '');
      if (str.includes('"') || str.includes(',') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvHeader = ['Department', 'Request Kind', 'Count', 'Others Detail'];
    const csvLines = [
      csvHeader.map(escapeCsv).join(','),
      ...rows.map((row) => row.map(escapeCsv).join(','))
    ];
    const csvContent = csvLines.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const rangeLabel = summaryDateFilter === 'today'
      ? `today-${getDateInputValue(new Date())}`
      : summaryDateFilter === 'custom' && summaryDateFrom && summaryDateTo
        ? `from-${summaryDateFrom}-to-${summaryDateTo}`
        : 'all-time';
    link.setAttribute('download', `service-request-summary-${rangeLabel}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (requestView === 'summary') {
      fetchSummary();
    }
  }, [requestView, summaryDateFilter, summaryDateFrom, summaryDateTo, summaryStatusFilter, fetchSummary]);

  // ── Socket-driven realtime updates — instant WebSocket push ──
  const socket = useSocket();
  useEffect(() => {
    // Initial load with spinner
    fetchRequests(false);

    if (socket) {
      const handleRealtimeUpdate = () => {
        fetchRequests(true);
      };

      socket.on('service_request_updated', handleRealtimeUpdate);
      socket.on('new_service_request', handleRealtimeUpdate);
      socket.on('connect', handleRealtimeUpdate);

      return () => {
        socket.off('service_request_updated', handleRealtimeUpdate);
        socket.off('new_service_request', handleRealtimeUpdate);
        socket.off('connect', handleRealtimeUpdate);
      };
    }
  }, [fetchRequests, socket]);

  // ── Read dashboard deep-link params (runs once on mount) ──
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const statusParam = searchParams.get('status');
    const viewParam = searchParams.get('view');
    const outcomeParam = searchParams.get('outcome') || searchParams.get('completion_status');
    const categoryParam = searchParams.get('category') || searchParams.get('department') || searchParams.get('activity');
    const dateParam   = searchParams.get('date');

    if (viewParam === 'dispute' || statusParam === 'dispute') {
      setRequestView('dispute');
    } else if (viewParam === 'cancelled' || statusParam === 'cancelled') {
      setRequestView('cancelled');
    } else if (statusParam === 'dnd') {
      setRequestView('completed');
      setCompletedOutcomeFilter('dnd');
    } else if (statusParam === 'critical' || viewParam === 'critical') {
      setRequestView('completed');
      setCompletedOutcomeFilter('critical');
    } else if (statusParam === 'escalated' || viewParam === 'escalated' || outcomeParam === 'escalated' || outcomeParam === 'escalated_to_hod') {
      setRequestView('completed');
      setCompletedOutcomeFilter('escalated_to_hod');
    } else if (statusParam === 'pending' || statusParam === 'completed' || statusParam === 'summary' || statusParam === 'all') {
      setRequestView(statusParam);
      if (outcomeParam) {
        setCompletedOutcomeFilter(outcomeParam);
      } else if (statusParam === 'completed') {
        setCompletedOutcomeFilter('all');
      }
    }

    if (categoryParam) {
      const normalizedCat = normalizeServiceRequestActivity(categoryParam) || categoryParam;
      if (normalizedCat) {
        setActiveTab(normalizedCat);
      }
    }

    if (dateParam) {
      // dateParam format: YYYY-MM-DD — set a single-day custom range
      setDateFilterMode('custom');
      setDateFrom(dateParam);
      setDateTo(dateParam);
    }

    // Clear the params from the URL so they don't persist on refresh
    if (statusParam || viewParam || outcomeParam || categoryParam || dateParam) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRequestActivity = (request) => {
    return normalizeServiceRequestActivity(request.category)
      || normalizeServiceRequestActivity(request.requested_activity)
      || normalizeServiceRequestActivity(request.metadata?.requested_activity)
      || normalizeServiceRequestActivity(request.metadata?.dispatch_activity)
      || 'Housekeeping Service';
  };

  const manualActivityOptions = useMemo(() => {
    if (user?.role === 'admin' || user?.role === 'manager' || user?.can_create_all_manual_requests) {
      return ALL_DEPARTMENTS.filter((department) => department.id !== 'all').map((department) => department.id);
    }
    return DEPARTMENTS.filter((department) => department.id !== 'all').map((department) => department.id);
  }, [ALL_DEPARTMENTS, DEPARTMENTS, user?.role, user?.can_create_all_manual_requests]);

  const getServiceOptionsForActivity = useCallback((activity) => {
    if (!activity || !flowSettings) return [{ id: 'others', title: 'Others', description: 'Custom request details' }];
    const norm = normalizeDepartment(activity);
    let rows = [];
    if (norm === 'Housekeeping Service') {
      rows = flowSettings.housekeeping_request_rows || [];
    } else if (norm === 'IT Support') {
      rows = flowSettings.it_support_request_rows || [];
    } else if (norm === 'Maintenance Assistance') {
      rows = flowSettings.maintenance_request_rows || [];
    } else if (norm === 'Front Office') {
      rows = flowSettings.front_office_request_rows || [];
    } else if (norm === 'Security Assistance') {
      rows = flowSettings.security_request_rows || [];
    }
    
    const activeRows = rows
      .filter((r) => r.enabled !== false && r.is_active !== false)
      .map((r) => ({
        id: r.id,
        title: r.title,
        description: r.description,
        detail_prompt_enabled: Boolean(r.detail_prompt_enabled),
        detail_prompt_message: r.detail_prompt_message || ''
      }));

    return [...activeRows, { id: 'others', title: 'Others', description: 'Type custom request details' }];
  }, [flowSettings]);

  const resetManualForm = useCallback((defaultActivity = null) => {
    const act = defaultActivity || manualActivityOptions[0] || '';
    const options = getServiceOptionsForActivity(act);
    setShowRoomWarningModal(false);
    setManualForm({
      guest_name: '',
      guest_room_no: '',
      guest_phone: '',
      activity: act,
      service_option_id: options[0]?.id || 'others',
      service_type: '',
      details: ''
    });
  }, [manualActivityOptions, getServiceOptionsForActivity]);

  const openManualModal = () => {
    resetManualForm();
    setManualModalOpen(true);
  };

  const closeManualModal = () => {
    if (manualSaving) return;
    setShowRoomWarningModal(false);
    setManualModalOpen(false);
  };

  const startCompletion = (request) => {
    setCompletingRequestId(request.id);
    setCompletionForm({ completion_status: '', work_done_by: '', remarks: '' });
  };

  const openRequestDetails = (request) => {
    setDetailsRequestId(request.id);
  };

  const closeRequestDetails = () => {
    setDetailsRequestId(null);
  };

  const handleConfirmCancel = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!cancellingRequest) return;
    if (!cancelReason.trim()) {
      setCancelError('Please enter a cancellation reason.');
      return;
    }
    try {
      setCancelSubmitting(true);
      setCancelError('');
      await api.post(`/api/roomservice/request/${cancellingRequest.id}/cancel`, {
        reason: cancelReason.trim()
      });
      setCancellingRequest(null);
      setCancelReason('');
      closeRequestDetails();
      fetchRequests(false);
    } catch (err) {
      console.error('Failed to cancel request:', err);
      setCancelError(err.response?.data?.error || 'Failed to cancel request');
    } finally {
      setCancelSubmitting(false);
    }
  };

  const openDisputeNoteModal = (request) => {
    setDisputeNoteModalRequest(request);
    setDisputeNoteText(request.metadata?.dispute_note || '');
    setDisputeNoteError('');
  };

  const closeDisputeNoteModal = () => {
    setDisputeNoteModalRequest(null);
    setDisputeNoteText('');
    setDisputeNoteError('');
  };

  const handleSaveDisputeNote = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!disputeNoteModalRequest) return;
    if (!disputeNoteText.trim()) {
      setDisputeNoteError('Please enter a note.');
      return;
    }
    try {
      setDisputeNoteSubmitting(true);
      setDisputeNoteError('');
      const res = await api.put(`/api/roomservice/request/${disputeNoteModalRequest.id}/dispute-note`, {
        note: disputeNoteText.trim()
      });

      const updatedRequest = res.data;
      setRequests((prev) =>
        prev.map((r) => (r.id === disputeNoteModalRequest.id ? { ...r, ...updatedRequest } : r))
      );

      closeDisputeNoteModal();
    } catch (err) {
      console.error('Failed to save dispute note:', err);
      setDisputeNoteError(err.response?.data?.error || 'Failed to save dispute note');
    } finally {
      setDisputeNoteSubmitting(false);
    }
  };

  const cancelCompletion = () => {
    setCompletingRequestId(null);
    setCompletionForm({ completion_status: '', work_done_by: '', remarks: '' });
  };

  const formatStaffSeenName = (s) => {
    if (!s) return '';
    if (
      (s.user_id && user?.id && s.user_id === user.id) ||
      (user?.name && s.name && s.name.toLowerCase().startsWith(user.name.toLowerCase()))
    ) {
      const userDesignation = user?.designation ? String(user.designation).trim() : null;
      const userRole = user?.role ? String(user.role).toUpperCase() : null;
      const desig = userDesignation || userRole;
      const baseName = user?.name || user?.username || 'Dashboard User';
      return desig ? `${baseName} (${desig})` : baseName;
    }
    return s.name || s.phone || 'Staff';
  };

  const isSeenByCurrentUser = (request) => {
    if (!request?.metadata?.staff_seen_by || !user) return false;
    const list = Array.isArray(request.metadata.staff_seen_by) ? request.metadata.staff_seen_by : [];
    const currentName = String(user.name || user.username || user.email || '').toLowerCase().trim();
    const currentId = user.id;
    return list.some((s) => {
      if (currentId && s.user_id && s.user_id === currentId) return true;
      if (currentName && s.name && s.name.toLowerCase().includes(currentName)) return true;
      return false;
    });
  };

  const handleMarkSeenFromDashboard = async (request) => {
    if (!request || !request.id) return;
    try {
      setMarkingSeenId(request.id);
      const res = await api.post(`/api/roomservice/request/${request.id}/mark-seen`);
      if (res.data?.success) {
        const userDisplayName = user?.name || user?.username || 'Dashboard User';
        const userRole = user?.role ? String(user.role).toUpperCase() : null;
        const userDesignation = user?.designation ? String(user.designation).trim() : null;
        const designationOrRole = userDesignation || userRole;
        const finalDisplayName = designationOrRole ? `${userDisplayName} (${designationOrRole})` : userDisplayName;
        const nowIso = new Date().toISOString();

        setRequests((prev) =>
          prev.map((r) => {
            if (r.id === request.id) {
              const prevSeen = Array.isArray(r.metadata?.staff_seen_by) ? [...r.metadata.staff_seen_by] : [];
              const alreadyInList = prevSeen.some(
                (s) => (s.user_id && user?.id && s.user_id === user.id) || (s.name && s.name.toLowerCase() === finalDisplayName.toLowerCase())
              );
              const updatedSeenBy = alreadyInList
                ? prevSeen
                : [...prevSeen, { user_id: user?.id || null, name: finalDisplayName, source: 'dashboard', seen_at: nowIso }];
              return {
                ...r,
                metadata: {
                  ...(r.metadata || {}),
                  staff_seen_by: updatedSeenBy,
                  last_staff_seen_at: nowIso
                }
              };
            }
            return r;
          })
        );
      }
    } catch (err) {
      console.error('Failed to mark request as seen from dashboard:', err);
    } finally {
      setMarkingSeenId(null);
    }
  };

  const submitDashboardCompletion = async (request) => {
    if (!request) return;
    if (!completionForm.completion_status) {
      alert('Please choose how this request was completed.');
      return;
    }
    if (!completionForm.work_done_by?.trim()) {
      alert('Please enter who the work was done by before submitting.');
      return;
    }
    try {
      setCompletionSavingId(request.id);
      await api.put(`/api/roomservice/request/${request.id}/staff-completion`, {
        completion_status: completionForm.completion_status,
        work_done_by: completionForm.work_done_by.trim(),
        remarks: completionForm.remarks.trim() || undefined
      });
      setCompletingRequestId(null);
      setCompletionForm({ completion_status: '', work_done_by: '', remarks: '' });
      await fetchRequests();
    } catch (error) {
      console.error('Failed to complete request:', error);
      alert(error.response?.data?.error || 'Failed to complete request. Please try again.');
    } finally {
      setCompletionSavingId(null);
    }
  };

  const executeCreateManualRequest = async () => {
    const currentOptions = getServiceOptionsForActivity(manualForm.activity);
    const selectedOption = currentOptions.find((o) => o.id === manualForm.service_option_id);
    const isOthers = manualForm.service_option_id === 'others';

    let finalDetails = '';
    let requestedActivity = manualForm.activity;

    if (isOthers) {
      finalDetails = manualForm.details.trim();
      requestedActivity = manualForm.activity;
    } else if (selectedOption) {
      requestedActivity = selectedOption.title;
      finalDetails = manualForm.details.trim()
        ? `${selectedOption.title}: ${manualForm.details.trim()}`
        : selectedOption.title;
    } else {
      finalDetails = manualForm.details.trim() || manualForm.activity;
    }

    try {
      setManualSaving(true);
      await api.post('/api/roomservice/request', {
        category: manualForm.activity,
        details: manualForm.service_type 
          ? `${manualForm.activity} (${manualForm.service_type}): ${finalDetails}`
          : finalDetails,
        metadata: {
          manual_request: true,
          requested_activity: requestedActivity,
          service_option_id: manualForm.service_option_id,
          service_type: manualForm.service_type || null
        },
        manual_guest: {
          name: manualForm.guest_name.trim(),
          room_no: manualForm.guest_room_no.trim(),
          ...(manualForm.guest_phone.trim() ? { phone: manualForm.guest_phone.trim() } : {})
        }
      });
      setShowRoomWarningModal(false);
      setManualModalOpen(false);
      await fetchRequests();
      alert('Manual request created successfully and staff notification flow started.');
    } catch (error) {
      console.error('Failed to create manual request:', error);
      alert(error.response?.data?.error || 'Failed to create manual request. Please try again.');
    } finally {
      setManualSaving(false);
    }
  };

  const submitManualRequest = async () => {
    const roomNo = manualForm.guest_room_no.trim();
    if (!roomNo) {
      alert('Please enter a room number.');
      return;
    }
    if (!manualForm.activity) {
      alert('Please select a department / activity.');
      return;
    }

    const isOthers = manualForm.service_option_id === 'others';
    if (isOthers && !manualForm.details.trim()) {
      alert('Please enter request details for "Others".');
      return;
    }

    const roomCheck = checkRoomExistsInPresetsOrQr(roomNo);
    if (!roomCheck.isValid) {
      setShowRoomWarningModal(true);
      return;
    }

    await executeCreateManualRequest();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRequestDisplayId = (request) => {
    const value = String(request?.id || '').trim();
    if (!value) return 'N/A';
    return value.slice(0, 8).toUpperCase();
  };

  const formatOptionalDateTime = (value) => {
    if (!value) return '--';
    try {
      return formatDateTime(value);
    } catch (error) {
      return '--';
    }
  };

  /** “Work done by” from WhatsApp Flow, else legacy completed_by_staff_name. */
  const getFlowWorkDoneBy = (request) => {
    const m = request?.metadata || {};
    return (
      (m.flow_work_done_by && String(m.flow_work_done_by).trim()) ||
      (request.completed_by_staff_name && String(request.completed_by_staff_name).trim()) ||
      ''
    );
  };

  const getCompletionResponseLower = (request) =>
    String(request.completion_response || request.metadata?.completion_response || '').toLowerCase();

  const getFlowRemarks = (request) => {
    const m = request?.metadata || {};
    return (m.flow_remarks && String(m.flow_remarks).trim()) || '';
  };

  /**
   * One-line summary for cards / dispatch contacts: reflects Flow choice
   * (Yes / No, DND / No, Critical Fix) plus optional work-done-by field.
   */
  const getStaffFormOutcomeSummary = (request) => {
    const cr = getCompletionResponseLower(request);
    const workBy = getFlowWorkDoneBy(request);
    const suffix = workBy ? ` · work done by: ${workBy}` : '';

    const dispatchStatus = request.dispatch_status || request.metadata?.dispatch_status;
    const criticalFixStatus = request.metadata?.critical_fix_status;

    if (criticalFixStatus === 'cancelled') {
      return {
        text: `Completed — Critical fix cancelled${suffix}`,
        className: 'text-green-700 font-semibold'
      };
    }
    if (criticalFixStatus === 'resolved') {
      return {
        text: `Critical fix (Resolved)${suffix}`,
        className: 'text-red-800 font-semibold'
      };
    }
    if (criticalFixStatus === 'confirmed') {
      return {
        text: `Critical fix (Confirmed)${suffix}`,
        className: 'text-red-800 font-semibold'
      };
    }

    if (cr === 'dnd' || dispatchStatus === 'dnd_notified') {
      return {
        text: `Not completed — “No, DND” (staff form)${suffix}`,
        className: 'text-amber-800 font-semibold'
      };
    }
    if (cr === 'critical' || dispatchStatus === 'critical_escalated') {
      return {
        text: `Critical fix — “No, Critical Fix” (staff form)${suffix}`,
        className: 'text-red-800 font-semibold'
      };
    }
    if (cr === 'no') {
      return {
        text: `Not completed — “No” (staff)${suffix}`,
        className: 'text-slate-700 font-semibold'
      };
    }
    if (cr === 'yes' || dispatchStatus === 'rating_pending' || dispatchStatus === 'rated') {
      return {
        text: workBy
          ? `Completed — “Yes” (staff form) · work done by: ${workBy}`
          : `Completed — “Yes” (staff form)`,
        className: 'text-green-700 font-semibold'
      };
    }
    if (request.status === 'completed') {
      return {
        text: workBy ? `Completed · work done by: ${workBy}` : 'Completed',
        className: 'text-green-700 font-semibold'
      };
    }
    return {
      text: `Updated${suffix}`,
      className: 'text-gray-700 font-semibold'
    };
  };

  const getStaffNameByPhone = (phone, defaultName = 'Staff') => {
    const target = normalizeDigits(phone);
    if (!target) return defaultName;
    const found = (departmentStaff || []).find((s) => normalizeDigits(s.phone) === target);
    return found?.name || defaultName;
  };

  const buildStaffAndHodDispatchTargets = (request) => {
    if (!request) return { staffTargets: [], hodTargets: [], isEscalated: false, escalatedAt: null };

    const metadata = request.metadata || {};
    const attempts = Array.isArray(request.dispatch_attempts) ? request.dispatch_attempts : [];

    // 1. STAFF TARGETS
    const staffMap = new Map();
    const addStaff = (phone, defaultName, badgeText, badgeClass, at) => {
      const p = String(phone || '').trim();
      const norm = normalizeDigits(p);
      if (!norm) return;

      const name = getStaffNameByPhone(p, defaultName);
      const existing = staffMap.get(norm);
      if (!existing) {
        staffMap.set(norm, {
          phone: p,
          name,
          badgeText: badgeText || 'Dispatched',
          badgeClass: badgeClass || 'bg-gray-100 text-gray-700',
          lastAt: at || null
        });
      } else {
        if (badgeText && badgeText !== 'Dispatched') {
          existing.badgeText = badgeText;
          existing.badgeClass = badgeClass || existing.badgeClass;
        }
        if (name && (!existing.name || existing.name === 'Staff')) {
          existing.name = name;
        }
        if (at) {
          const nextTime = new Date(at).getTime();
          const prevTime = existing.lastAt ? new Date(existing.lastAt).getTime() : 0;
          if (!Number.isNaN(nextTime) && nextTime >= prevTime) {
            existing.lastAt = at;
          }
        }
        staffMap.set(norm, existing);
      }
    };

    const primaryPhones = Array.isArray(metadata.dispatch_primary_phones)
      ? metadata.dispatch_primary_phones
      : (metadata.dispatch_primary_phone ? [metadata.dispatch_primary_phone] : []);
    primaryPhones.forEach((p) => addStaff(p, 'Staff', 'Dispatched', 'bg-blue-50 text-blue-700 border border-blue-200', request.created_at));

    attempts.forEach((att) => {
      if (att.primary_phone) {
        addStaff(att.primary_phone, 'Staff', 'Dispatched', 'bg-blue-50 text-blue-700 border border-blue-200', att.created_at);
      }
      if (att.acknowledged_by_phone) {
        addStaff(att.acknowledged_by_phone, 'Staff', 'Responded', 'bg-indigo-50 text-indigo-700 border border-indigo-200', att.acknowledged_at);
      }
    });

    if (metadata.staff_acknowledged_by_phone) {
      addStaff(
        metadata.staff_acknowledged_by_phone,
        metadata.staff_acknowledged_by_name || 'Staff',
        'Responded',
        'bg-indigo-50 text-indigo-700 border border-indigo-200',
        metadata.staff_acknowledged_at
      );
    }

    if (metadata.completed_by_staff_phone) {
      addStaff(
        metadata.completed_by_staff_phone,
        metadata.completed_by_staff_name || 'Staff',
        'Completed Request',
        'bg-green-50 text-green-700 border border-green-200',
        metadata.completed_by_staff_at
      );
    }

    // 2. HOD ESCALATION TARGETS
    const hodMap = new Map();
    let isEscalated = false;
    let escalatedAt = null;

    if (hasValidEscalation(request)) {
      isEscalated = true;
      escalatedAt = metadata.dispatch_escalated_at || attempts.find((a) => a.escalated_at)?.escalated_at || null;
    }

    const addHod = (phone, defaultName, at) => {
      const p = String(phone || '').trim();
      const norm = normalizeDigits(p);
      if (!norm) return;

      const name = getStaffNameByPhone(p, defaultName);
      const existing = hodMap.get(norm);
      if (!existing) {
        hodMap.set(norm, {
          phone: p,
          name,
          lastAt: at || null
        });
      } else {
        if (name && (!existing.name || existing.name === 'HOD')) {
          existing.name = name;
        }
        if (at) {
          const nextTime = new Date(at).getTime();
          const prevTime = existing.lastAt ? new Date(existing.lastAt).getTime() : 0;
          if (!Number.isNaN(nextTime) && nextTime >= prevTime) {
            existing.lastAt = at;
          }
        }
        hodMap.set(norm, existing);
      }
    };

    const escalationPhones = Array.isArray(metadata.dispatch_escalation_phones)
      ? metadata.dispatch_escalation_phones
      : (metadata.dispatch_escalation_phone ? [metadata.dispatch_escalation_phone] : []);
    escalationPhones.forEach((p) => addHod(p, 'HOD', escalatedAt));

    if (Array.isArray(metadata.escalated_to_phones)) {
      metadata.escalated_to_phones.forEach((p) => addHod(p, 'HOD', escalatedAt));
    }

    attempts.forEach((att) => {
      const attEscPhones = Array.isArray(att.escalation_phones) && att.escalation_phones.length > 0
        ? att.escalation_phones
        : (att.escalation_phone ? [att.escalation_phone] : []);
      attEscPhones.forEach((p) => addHod(p, 'HOD', att.escalated_at));
      if (att.escalated_at) {
        isEscalated = true;
        if (!escalatedAt) escalatedAt = att.escalated_at;
      }
    });

    // 3. SUPER HOD TARGETS
    const superHodMap = new Map();
    let isSuperEscalated = false;
    let superEscalatedAt = null;

    if (metadata.dispatch_status === 'super_escalated' || attempts.some((a) => a.super_escalated_at)) {
      isSuperEscalated = true;
      superEscalatedAt = metadata.dispatch_super_escalated_at || attempts.find((a) => a.super_escalated_at)?.super_escalated_at || null;
    }

    const addSuperHod = (phone, defaultName, at) => {
      const p = String(phone || '').trim();
      const norm = normalizeDigits(p);
      if (!norm) return;

      const name = getStaffNameByPhone(p, defaultName);
      const existing = superHodMap.get(norm);
      if (!existing) {
        superHodMap.set(norm, {
          phone: p,
          name,
          lastAt: at || null
        });
      } else {
        if (name && (!existing.name || existing.name === 'Super HOD')) {
          existing.name = name;
        }
        if (at) {
          const nextTime = new Date(at).getTime();
          const prevTime = existing.lastAt ? new Date(existing.lastAt).getTime() : 0;
          if (!Number.isNaN(nextTime) && nextTime >= prevTime) {
            existing.lastAt = at;
          }
        }
        superHodMap.set(norm, existing);
      }
    };

    const superPhones = Array.isArray(metadata.dispatch_super_hod_phones)
      ? metadata.dispatch_super_hod_phones
      : (metadata.dispatch_super_hod_phone
          ? [metadata.dispatch_super_hod_phone]
          : (Array.isArray(metadata.super_hod_phones)
              ? metadata.super_hod_phones
              : (metadata.super_hod_phone ? [metadata.super_hod_phone] : [])));
    superPhones.forEach((p) => addSuperHod(p, 'Super HOD', superEscalatedAt));

    attempts.forEach((att) => {
      const attSuperPhones = Array.isArray(att.super_hod_phones) && att.super_hod_phones.length > 0
        ? att.super_hod_phones
        : (att.super_hod_phone ? [att.super_hod_phone] : []);
      attSuperPhones.forEach((p) => addSuperHod(p, 'Super HOD', att.super_escalated_at));
      if (att.super_escalated_at) {
        isSuperEscalated = true;
        if (!superEscalatedAt) superEscalatedAt = att.super_escalated_at;
      }
    });

    return {
      staffTargets: Array.from(staffMap.values()).filter(
        (t) => !hiddenStaffPhones.has(normalizeDigits(t.phone))
      ),
      hodTargets: Array.from(hodMap.values()).filter(
        (t) => !hiddenStaffPhones.has(normalizeDigits(t.phone))
      ),
      superHodTargets: Array.from(superHodMap.values()).filter(
        (t) => !hiddenStaffPhones.has(normalizeDigits(t.phone))
      ),
      isEscalated,
      escalatedAt,
      isSuperEscalated,
      superEscalatedAt
    };
  };

  const getHodDisplay = (request) => {
    if (!request) return '—';
    const metadata = request.metadata || {};
    const attempts = Array.isArray(request.dispatch_attempts) ? request.dispatch_attempts : [];

    const rawHodPhones = Array.isArray(metadata.dispatch_escalation_phones) && metadata.dispatch_escalation_phones.length > 0
      ? metadata.dispatch_escalation_phones
      : (metadata.dispatch_escalation_phone
          ? [metadata.dispatch_escalation_phone]
          : (Array.isArray(metadata.escalated_to_phones) && metadata.escalated_to_phones.length > 0
              ? metadata.escalated_to_phones
              : []));

    let hodPhones = [...rawHodPhones];
    if (hodPhones.length === 0) {
      attempts.forEach((att) => {
        const attHodPhones = Array.isArray(att.escalation_phones) && att.escalation_phones.length > 0
          ? att.escalation_phones
          : (att.escalation_phone ? [att.escalation_phone] : []);
        hodPhones.push(...attHodPhones);
      });
    }

    const uniqueHodPhones = Array.from(new Set(hodPhones.filter(Boolean)));
    if (uniqueHodPhones.length === 0) return '—';

    const names = uniqueHodPhones.map((p) => getStaffNameByPhone(p, 'HOD'));
    return names.join(', ');
  };

  const getSuperHodDisplay = (request) => {
    if (!request) return '—';
    const metadata = request.metadata || {};
    const attempts = Array.isArray(request.dispatch_attempts) ? request.dispatch_attempts : [];

    const rawSuperPhones = Array.isArray(metadata.dispatch_super_hod_phones) && metadata.dispatch_super_hod_phones.length > 0
      ? metadata.dispatch_super_hod_phones
      : (metadata.dispatch_super_hod_phone
          ? [metadata.dispatch_super_hod_phone]
          : (Array.isArray(metadata.super_hod_phones) && metadata.super_hod_phones.length > 0
              ? metadata.super_hod_phones
              : (metadata.super_hod_phone ? [metadata.super_hod_phone] : [])));

    let superPhones = [...rawSuperPhones];
    if (superPhones.length === 0) {
      attempts.forEach((att) => {
        const attSuperPhones = Array.isArray(att.super_hod_phones) && att.super_hod_phones.length > 0
          ? att.super_hod_phones
          : (att.super_hod_phone ? [att.super_hod_phone] : []);
        superPhones.push(...attSuperPhones);
      });
    }

    const uniqueSuperPhones = Array.from(new Set(superPhones.filter(Boolean)));
    if (uniqueSuperPhones.length === 0) return '—';

    const names = uniqueSuperPhones.map((p) => getStaffNameByPhone(p, 'Super HOD'));
    return names.join(', ');
  };

  const buildDispatchContacts = (request) => {
    const targets = buildStaffAndHodDispatchTargets(request);
    return [...targets.staffTargets, ...targets.hodTargets, ...(targets.superHodTargets || [])];
  };

  const hasValidEscalation = (request) => {
    const escalatedAt = request?.metadata?.dispatch_escalated_at || request?.dispatch_attempts?.find((a) => a.escalated_at)?.escalated_at;
    if (!escalatedAt) return false;
    
    const completedAt = request.metadata?.completed_by_staff_at || request.completion_response_at || request.metadata?.completion_response_at;
    if (completedAt && new Date(completedAt) < new Date(escalatedAt)) {
      return false;
    }
    return true;
  };

  const getDispatchStatusLabel = (request) => {
    const status = request.dispatch_status || request.metadata?.dispatch_status;
    switch (status) {
      case 'flow_pending':
      case 'acknowledgement_pending':
        return { text: 'Awaiting Staff Response', classes: 'bg-amber-100 text-amber-800' };
      case 'completion_pending':
        return { text: 'Completion Pending', classes: 'bg-blue-100 text-blue-800' };
      case 'awaiting_completion_image':
        return { text: 'Awaiting Completion Photo', classes: 'bg-orange-100 text-orange-800' };
      case 'rating_pending':
        return { text: 'Rating Pending', classes: 'bg-indigo-100 text-indigo-800' };
      case 'rated':
        return { text: 'Rated', classes: 'bg-emerald-100 text-emerald-800' };
      case 'escalated':
        if (!hasValidEscalation(request)) return null;
        return { text: 'Escalated to HOD', classes: 'bg-red-100 text-red-800' };
      case 'dnd_notified':
        return { text: 'DND — not completed', classes: 'bg-amber-100 text-amber-900 ring-1 ring-amber-300' };
      case 'critical_escalated':
        if (request.metadata?.critical_fix_status === 'cancelled') {
          return { text: 'Critical Fix Cancelled — Completed', classes: 'bg-green-100 text-green-800' };
        }
        if (request.metadata?.critical_fix_status === 'resolved') {
          return { text: 'Critical fix — Resolved', classes: 'bg-red-100 text-red-900 ring-1 ring-red-300' };
        }
        if (request.metadata?.critical_fix_status === 'confirmed') {
          return { text: 'Critical fix — Confirmed', classes: 'bg-red-100 text-red-900 ring-1 ring-red-300' };
        }
        return { text: 'Critical fix — escalated', classes: 'bg-red-100 text-red-900 ring-1 ring-red-300' };
      case 'guest_disputed':
        return { text: 'Guest disputed completion', classes: 'bg-orange-100 text-orange-900 ring-1 ring-orange-300' };
      case 'dashboard_confirmed':
        return { text: 'Confirmed (dashboard)', classes: 'bg-emerald-100 text-emerald-800' };
      case 'completed':
        return { text: 'Dispatch Completed', classes: 'bg-green-100 text-green-800' };
      default:
        return null;
    }
  };

  const getRequestBucket = (request) => {
    if (request.status === 'cancelled') return 'cancelled';
    const dispatchStatus = request.dispatch_status || request.metadata?.dispatch_status;
    const completionResponse = (request.completion_response || request.metadata?.completion_response || '').toLowerCase();
    const normalized = normalizeStatus(request.status);

    if (dispatchStatus === 'guest_disputed') return 'dispute';
    if (normalized === 'completed') return 'completed';
    if (['yes', 'no', 'dnd', 'critical'].includes(completionResponse)) return 'completed';
    if (dispatchStatus === 'dnd_notified' || dispatchStatus === 'critical_escalated') return 'completed';
    return 'pending';
  };

  /** WhatsApp staff completion follow-up: Yes / No / DND / Critical (+ guest dispute after “completed”). */
  const getStaffCompletionCheckLabel = (request) => {
    const cr = request.completion_response || request.metadata?.completion_response;
    if (!cr) return null;
    const workBy = getFlowWorkDoneBy(request);
    const w = workBy ? ` · ${workBy}` : '';
    const crLower = String(cr).toLowerCase();

    if (crLower === 'critical') {
      if (request.metadata?.critical_fix_status === 'cancelled') {
        return { text: `Staff: “No, Critical Fix” (Cancelled · Completed)${w}`, classes: 'bg-green-50 text-green-900 ring-1 ring-green-200' };
      }
      if (request.metadata?.critical_fix_status === 'resolved') {
        return { text: `Staff: “No, Critical Fix” (Resolved)${w}`, classes: 'bg-red-50 text-red-900 ring-1 ring-red-200' };
      }
      if (request.metadata?.critical_fix_status === 'confirmed') {
        return { text: `Staff: “No, Critical Fix” (Confirmed)${w}`, classes: 'bg-red-50 text-red-900 ring-1 ring-red-200' };
      }
    }

    const map = {
      yes: { text: `Staff: “Yes” (complete)${w}`, classes: 'bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200' },
      no: { text: `Staff: “No”${w}`, classes: 'bg-slate-100 text-slate-800 ring-1 ring-slate-200' },
      dnd: { text: `Staff: “No, DND”${w}`, classes: 'bg-amber-50 text-amber-900 ring-1 ring-amber-200' },
      critical: { text: `Staff: “No, Critical Fix”${w}`, classes: 'bg-red-50 text-red-900 ring-1 ring-red-200' }
    };
    return map[crLower] || null;
  };

  const normalizeStatus = (status) => {
    if (status === 'received') return 'pending';
    if (status === 'in-progress') return 'pending';
    return status;
  };

  const getPendingSince = (request) => {
    const pendingStart = request.pending_at || request.created_at;
    if (!pendingStart) return '--';

    const startedAt = parseServerDate(pendingStart);
    if (!startedAt) return '--';

    const diffMs = Date.now() - startedAt.getTime();
    const minutes = Math.max(0, Math.floor(diffMs / 60000));
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const getPendingMinutes = (request) => {
    const pendingStart = request.pending_at || request.created_at;
    if (!pendingStart) return 0;
    const startedAt = parseServerDate(pendingStart);
    if (!startedAt) return 0;
    return Math.max(0, Math.floor((Date.now() - startedAt.getTime()) / 60000));
  };

  const formatDuration = (minutes) => {
    if (!minutes || minutes <= 0) return '0 min';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const getCategoryColor = (category) => {
    const dept = DEPARTMENTS.find(d => d.id === category);
    if (!dept) return 'border border-gray-200 text-gray-500';
    
    const colorMap = {
      purple: 'border border-purple-200 text-purple-700',
      orange: 'border border-orange-200 text-orange-700',
      blue: 'border border-blue-200 text-blue-700',
      green: 'border border-green-200 text-green-700',
      red: 'border border-red-200 text-red-600',
      yellow: 'border border-yellow-200 text-yellow-700',
      pink: 'border border-pink-200 text-pink-700',
      indigo: 'border border-indigo-200 text-indigo-700',
      emerald: 'border border-emerald-200 text-emerald-700',
      amber: 'border border-amber-200 text-amber-700',
      gray: 'border border-gray-200 text-gray-500'
    };
    return colorMap[dept.color] || 'border border-gray-200 text-gray-500';
  };

  const isWithinDateRange = useCallback((isoString) => {
    if (!isoString) return true;
    const reqDateIST = formatIST(isoString, 'yyyy-MM-dd');
    if (!reqDateIST) return true;

    if (dateFilterMode === 'today') {
      const todayIST = getDateInputValue(new Date());
      return reqDateIST === todayIST;
    }
    if (dateFilterMode === 'last7') {
      const todayIST = getDateInputValue(new Date());
      const last7Date = new Date();
      last7Date.setDate(last7Date.getDate() - 6);
      const last7IST = getDateInputValue(last7Date);
      return reqDateIST >= last7IST && reqDateIST <= todayIST;
    }
    if (dateFilterMode === 'custom') {
      if (!dateFrom || !dateTo) return true;
      return reqDateIST >= dateFrom && reqDateIST <= dateTo;
    }
    return true;
  }, [dateFilterMode, dateFrom, dateTo]);

  const getScopedRequests = () => {
    const activityFiltered = activeTab === 'all'
      ? requests
      : requests.filter((req) => getRequestActivity(req) === activeTab);

    return activityFiltered
      .filter((req) => isWithinDateRange(req.created_at))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  };

  const getRequestsByTab = () => {
    const scoped = getScopedRequests();
    const tabFiltered = scoped.filter((req) => {
      const bucket = getRequestBucket(req);
      if (requestView === 'pending') return bucket === 'pending';
      if (requestView === 'completed') {
        if (bucket !== 'completed' && bucket !== 'dispute') return false;
        if (completedOutcomeFilter === 'all') return true;
        const ds = req.dispatch_status || req.metadata?.dispatch_status;
        const cr = String(req.completion_response || req.metadata?.completion_response || '').toLowerCase();
        if (completedOutcomeFilter === 'yes') {
          const isDnd = cr === 'dnd' || ds === 'dnd_notified';
          const isCritical = (cr === 'critical' || ds === 'critical_escalated' || req.metadata?.critical_fix_status === 'confirmed' || req.metadata?.critical_fix_status === 'resolved') && req.metadata?.critical_fix_status !== 'cancelled';
          const isNo = cr === 'no';
          const isDispute = ds === 'guest_disputed';
          if (isDnd || isCritical || isNo || isDispute) return false;
          return cr === 'yes' || ds === 'rating_pending' || ds === 'rated' || bucket === 'completed' || req.status === 'completed';
        }
        if (completedOutcomeFilter === 'dnd') return cr === 'dnd' || ds === 'dnd_notified';
        if (completedOutcomeFilter === 'critical') return (cr === 'critical' || ds === 'critical_escalated' || req.metadata?.critical_fix_status === 'confirmed' || req.metadata?.critical_fix_status === 'resolved') && req.metadata?.critical_fix_status !== 'cancelled';
        if (completedOutcomeFilter === 'dispute') return ds === 'guest_disputed' || bucket === 'dispute';
        if (completedOutcomeFilter === 'escalated_to_hod') {
          return ds === 'escalated' || ds === 'super_escalated' || ds === 'critical_escalated' ||
            !!req.metadata?.escalated_at || !!req.metadata?.dispatch_escalated_at || !!req.metadata?.super_escalated_at || !!req.escalated_at;
        }
        return true;
      }
      if (requestView === 'dispute') return bucket === 'dispute';
      if (requestView === 'cancelled') return bucket === 'cancelled' || req.status === 'cancelled';
      return true;
    });

    const q = String(searchQuery || '').trim().toLowerCase();
    if (!q) return tabFiltered;

    return tabFiltered.filter((req) => {
      const roomNo = String(req.room_no || '').toLowerCase();
      const reqId = String(req.id || '').toLowerCase();
      const reqShortId = getRequestDisplayId(req).toLowerCase();
      return roomNo.includes(q) || reqId.includes(q) || reqShortId.includes(q);
    });
  };

  const downloadRequestsCsv = () => {
    if (!filteredRequests || filteredRequests.length === 0) {
      alert('No service requests to export for the current filters.');
      return;
    }

    const cleanText = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();
    const escapeCsv = (value) => {
      const str = String(value ?? '');
      if (str.includes('"') || str.includes(',') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const isCancelledView = requestView === 'cancelled';
    const isDisputeView = requestView === 'dispute';
    const header = isCancelledView
      ? ['Date', 'Room Number', 'Department', 'Request Details', 'Cancelled By', 'Cancellation Reason', 'Cancelled At']
      : isDisputeView
        ? ['Date', 'Room Number', 'Department', 'Request Details', 'Dispute Note', 'Note Written By', 'Note Written At']
        : ['Date', 'Room Number', 'Department', 'Request Details', 'Work Done By', 'Remarks', 'Dispute Note'];

    const rows = filteredRequests.map((request) => (
      isCancelledView
        ? [
            formatOptionalDateTime(request.created_at),
            request.room_no || 'N/A',
            getRequestActivity(request) || 'N/A',
            cleanText(request.details) || 'N/A',
            cleanText(request.metadata?.cancelled_by_user_name) || 'N/A',
            cleanText(request.metadata?.cancellation_reason) || 'N/A',
            formatOptionalDateTime(request.metadata?.cancelled_at) || 'N/A'
          ]
        : isDisputeView
          ? [
              formatOptionalDateTime(request.created_at),
              request.room_no || 'N/A',
              getRequestActivity(request) || 'N/A',
              cleanText(request.details) || 'N/A',
              cleanText(request.metadata?.dispute_note) || 'N/A',
              cleanText(request.metadata?.dispute_note_by_user_name) || 'N/A',
              formatOptionalDateTime(request.metadata?.dispute_note_at) || 'N/A'
            ]
          : [
              formatOptionalDateTime(request.created_at),
              request.room_no || 'N/A',
              getRequestActivity(request) || 'N/A',
              cleanText(request.details) || 'N/A',
              cleanText(getFlowWorkDoneBy(request)) || 'N/A',
              cleanText(getFlowRemarks(request)) || 'N/A',
              cleanText(request.metadata?.dispute_note) || 'N/A'
            ]
    ));

    const csv = [header, ...rows].map((row) => row.map(escapeCsv).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `service-requests-${requestView}-${dateFrom || 'from'}-to-${dateTo || 'to'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadRequestsPdf = async () => {
    if (!filteredRequests || filteredRequests.length === 0) {
      alert('No service requests to export for the current filters.');
      return;
    }

    try {
      setIsExportingPdf(true);

      const activeDeptObj = DEPARTMENTS.find((d) => d.id === activeTab);
      const deptName = user?.role === 'department' && user?.department
        ? user.department
        : (activeTab !== 'all' ? (activeDeptObj?.name || activeTab) : 'All Departments');

      let dateRangeText = 'All Available Dates';
      if (dateFilterMode === 'today') {
        dateRangeText = 'Today';
      } else if (dateFilterMode === 'last7') {
        dateRangeText = 'Last 7 Days';
      } else if (dateFilterMode === 'custom') {
        if (dateFrom && dateTo) {
          dateRangeText = `${dateFrom} to ${dateTo}`;
        } else if (dateFrom) {
          dateRangeText = `From ${dateFrom}`;
        } else if (dateTo) {
          dateRangeText = `Up to ${dateTo}`;
        }
      }

      const safeDeptName = String(deptName).replace(/[^a-zA-Z0-9_-]/g, '_');
      const safeView = (requestView === 'completed' && completedOutcomeFilter !== 'all')
        ? `completed_${completedOutcomeFilter}`
        : (requestView || 'all').toLowerCase();
      const dateSuffix = dateFilterMode === 'custom' && dateFrom && dateTo
        ? `${dateFrom}_to_${dateTo}`
        : dateFilterMode;
      const fileName = `Service_Requests_${safeDeptName}_${safeView}_${dateSuffix}.pdf`;

      await exportServiceRequestsPdf({
        requests: filteredRequests,
        filterContext: {
          department: deptName,
          view: requestView,
          outcome: completedOutcomeFilter,
          dateRangeText
        },
        fileName
      });
    } catch (err) {
      console.error('Failed to export service requests PDF:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsExportingPdf(false);
    }
  };

  const getRequestCounts = () => {
    const counts = {};
    DEPARTMENTS.forEach(dept => {
      if (dept.id === 'all') {
        counts[dept.id] = requests.length;
      } else {
        counts[dept.id] = requests.filter(r => getRequestActivity(r) === dept.id).length;
      }
    });
    return counts;
  };

  const counts = getRequestCounts();
  const scopedRequests = getScopedRequests();
  const filteredRequests = getRequestsByTab();
  const activeCompletionRequest = requests.find((request) => request.id === completingRequestId) || null;
  const activeDetailsRequest = requests.find((request) => request.id === detailsRequestId) || null;
  const activeDetailsTargets = activeDetailsRequest
    ? buildStaffAndHodDispatchTargets(activeDetailsRequest)
    : { staffTargets: [], hodTargets: [], isEscalated: false, escalatedAt: null };
  const activeDetailsDispatchContacts = activeDetailsRequest ? buildDispatchContacts(activeDetailsRequest) : [];
  const pendingCount = scopedRequests.filter((request) => getRequestBucket(request) === 'pending').length;
  const completedCount = scopedRequests.filter((request) => getRequestBucket(request) === 'completed').length;
  const disputeCount = scopedRequests.filter((request) => getRequestBucket(request) === 'dispute').length;
  const cancelledCount = scopedRequests.filter((request) => getRequestBucket(request) === 'cancelled').length;
  const avgPendingMinutes = pendingCount > 0
    ? Math.round(
        scopedRequests
          .filter((request) => getRequestBucket(request) === 'pending')
          .reduce((sum, request) => sum + getPendingMinutes(request), 0) / pendingCount
      )
    : 0;

  const selectedTabCount = totalRequests;

  const outcomeLabelMap = {
    all: 'completed',
    yes: 'completed (Yes)',
    dnd: 'No, DND',
    critical: 'No, Critical Fix',
    dispute: 'service dispute',
    escalated_to_hod: 'escalated to HOD'
  };

  const selectedTabLabel = requestView === 'pending'
    ? 'pending'
    : requestView === 'completed'
      ? (completedOutcomeFilter !== 'all' ? outcomeLabelMap[completedOutcomeFilter] || 'completed' : 'completed')
      : requestView === 'dispute'
        ? 'service dispute'
        : requestView === 'cancelled'
          ? 'cancelled'
          : 'all';

  if (loading && requests.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5 px-2 py-3 sm:px-3 sm:py-4 lg:px-1 lg:py-2">
        <div className="space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-1">
              {user?.role === 'department' && user?.department 
                ? `${user.department} Service Requests` 
                : 'Service Requests'}
              </h1>
              <p className="text-sm text-gray-500">Track every request lifecycle with cleaner visibility, faster assignment, and smarter completion flow.</p>
              {user?.role === 'department' && user?.department && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 border border-gray-200">
                    Department: {user.department}
                  </span>
                  <span className="text-xs text-gray-500">
                    Activity: {normalizeDepartment(user.department)}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-2 w-full lg:w-auto">
              {!isActivityBookingDepartmentUser && (
                <div className="flex items-center justify-end">
                  <button
                    onClick={openManualModal}
                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition-colors"
                  >
                    <FiPlus className="h-4 w-4" />
                    Manual Request
                  </button>
                </div>
              )}
              {lastUpdatedAt && (
                <p className="text-xs text-gray-500 text-right">Updated: {formatDateTime(lastUpdatedAt)}</p>
              )}
            </div>
          </div>

        </div>

        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-4 sm:p-5 space-y-4">
          {/* Top Row: Primary Navigation Pills */}
          <div className="flex items-center justify-between gap-3 overflow-x-auto pb-1 sm:pb-0 scrollbar-none">
            <div className="flex items-center p-1 bg-gray-100/90 rounded-xl gap-1 min-w-max">
              {[
                { id: 'pending', label: 'Pending' },
                { id: 'completed', label: 'Completed' },
                { id: 'dispute', label: 'Service Dispute', isWarning: true },
                { id: 'all', label: 'All Requests' },
                { id: 'summary', label: 'Summary Report' },
                { id: 'cancelled', label: 'Cancelled' }
              ].map((view) => {
                const isActive = requestView === view.id;
                return (
                  <button
                    key={view.id}
                    onClick={() => {
                      setRequestView(view.id);
                      setCompletedOutcomeFilter('all');
                      setRequests([]);
                      setPage(1);
                    }}
                    className={`px-3.5 py-2 rounded-lg text-xs font-semibold tracking-wide transition-all duration-200 flex items-center gap-1.5 ${
                      isActive
                        ? view.isWarning
                          ? 'bg-orange-600 text-white shadow-sm'
                          : 'bg-gray-900 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/70'
                    }`}
                  >
                    {view.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Completed Outcome Sub-filter pills (Smooth Expand) */}
          {requestView === 'completed' && (
            <div className="flex items-center gap-2 flex-wrap bg-gray-50/90 p-2.5 rounded-xl border border-gray-100">
              <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider pl-1">Outcome:</span>
              {[
                { id: 'all', label: 'All Completed' },
                { id: 'yes', label: 'Completed (Yes)', color: 'green' },
                { id: 'dnd', label: 'No, DND', color: 'amber' },
                { id: 'critical', label: 'No, Critical Fix', color: 'red' },
                { id: 'dispute', label: 'Service Dispute', color: 'orange' },
                { id: 'escalated_to_hod', label: 'Escalated to HOD', color: 'purple' }
              ].map((opt) => {
                const isActive = completedOutcomeFilter === opt.id;
                let activeStyle = 'bg-gray-900 text-white border-gray-900 shadow-sm';
                if (isActive) {
                  if (opt.color === 'green') activeStyle = 'bg-green-600 text-white border-green-600 shadow-sm';
                  if (opt.color === 'amber') activeStyle = 'bg-amber-600 text-white border-amber-600 shadow-sm';
                  if (opt.color === 'red') activeStyle = 'bg-red-600 text-white border-red-600 shadow-sm';
                  if (opt.color === 'orange') activeStyle = 'bg-orange-600 text-white border-orange-600 shadow-sm';
                  if (opt.color === 'purple') activeStyle = 'bg-purple-600 text-white border-purple-600 shadow-sm';
                }
                return (
                  <button
                    key={opt.id}
                    onClick={() => {
                      setCompletedOutcomeFilter(opt.id);
                      setPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                      isActive
                        ? activeStyle
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Bottom Row: Search, Department Filter, Date Range */}
          {requestView !== 'summary' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-2.5 items-center">
              {/* Search Box */}
              <div className="relative col-span-1 sm:col-span-2 lg:col-span-5">
                <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search room no or request ID..."
                  className="w-full pl-10 pr-9 py-2.5 bg-gray-50/70 hover:bg-gray-50 focus:bg-white border border-gray-200 rounded-xl text-xs sm:text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-900 transition-all"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      setPage(1);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 rounded-md"
                  >
                    <FiX className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {/* Department Dropdown */}
              <div className="relative col-span-1 lg:col-span-4">
                <FiLayers className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
                <select
                  value={activeTab}
                  onChange={(e) => {
                    setActiveTab(e.target.value);
                    setPage(1);
                  }}
                  className="w-full pl-10 pr-8 py-2.5 bg-gray-50/70 hover:bg-gray-50 focus:bg-white border border-gray-200 rounded-xl text-xs sm:text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-900 transition-all appearance-none cursor-pointer"
                >
                  {DEPARTMENTS.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name} {dept.id === activeTab ? `(${totalRequests})` : ''}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                  <FiChevronDown className="h-4 w-4" />
                </div>
              </div>

              {/* Date Filter Dropdown */}
              <div className="relative col-span-1 lg:col-span-3">
                <FiCalendar className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
                <select
                  value={dateFilterMode}
                  onChange={(e) => {
                    setDateFilterMode(e.target.value);
                    setPage(1);
                  }}
                  className="w-full pl-10 pr-8 py-2.5 bg-gray-50/70 hover:bg-gray-50 focus:bg-white border border-gray-200 rounded-xl text-xs sm:text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-900 transition-all appearance-none cursor-pointer"
                >
                  <option value="all">All Dates</option>
                  <option value="today">Today</option>
                  <option value="last7">Last 7 Days</option>
                  <option value="custom">Custom Range</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                  <FiChevronDown className="h-4 w-4" />
                </div>
              </div>

              {/* Custom Date Pickers (when custom range is chosen) */}
              {dateFilterMode === 'custom' && (
                <div className="col-span-1 sm:col-span-2 lg:col-span-12 grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
                  <div>
                    <span className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">From Date</span>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => {
                        setDateFrom(e.target.value);
                        setPage(1);
                      }}
                      className="w-full px-3 py-2 bg-gray-50 focus:bg-white border border-gray-200 rounded-xl text-xs sm:text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-900 transition-all"
                    />
                  </div>
                  <div>
                    <span className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">To Date</span>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => {
                        setDateTo(e.target.value);
                        setPage(1);
                      }}
                      className="w-full px-3 py-2 bg-gray-50 focus:bg-white border border-gray-200 rounded-xl text-xs sm:text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-900 transition-all"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-4">
          {requestView === 'summary' ? (
            <div className="fleet-card p-6 bg-white overflow-hidden">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-gray-100">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Department-wise Request Summary</h3>
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                    {summaryDateFilter === 'today'
                      ? `Showing requests for Today (${getDateInputValue(new Date())})`
                      : summaryDateFilter === 'custom' && summaryDateFrom && summaryDateTo
                        ? `Showing requests from ${summaryDateFrom} to ${summaryDateTo}`
                        : 'Showing all-time requests across all departments'}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2.5">
                  {/* Date Filter Dropdown */}
                  <select
                    value={summaryDateFilter}
                    onChange={(e) => {
                      const mode = e.target.value;
                      setSummaryDateFilter(mode);
                      if (mode === 'custom' && !summaryDateFrom && !summaryDateTo) {
                        const today = getDateInputValue(new Date());
                        setSummaryDateFrom(today);
                        setSummaryDateTo(today);
                      }
                    }}
                    className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-xs sm:text-sm bg-white font-medium focus:ring-1 focus:ring-gray-400 focus:outline-none"
                  >
                    <option value="all">All Time (Default)</option>
                    <option value="today">Today</option>
                    <option value="custom">Custom Date Range</option>
                  </select>

                  {/* Custom Date Pickers (only when Custom Date Range is selected) */}
                  {summaryDateFilter === 'custom' && (
                    <div className="flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-lg border border-gray-200 text-xs">
                      <span className="text-gray-500 font-medium">From:</span>
                      <input
                        type="date"
                        value={summaryDateFrom}
                        onChange={(e) => setSummaryDateFrom(e.target.value)}
                        className="px-2 py-1 bg-white border border-gray-200 rounded text-xs text-gray-800 focus:outline-none focus:border-gray-400"
                      />
                      <span className="text-gray-500 font-medium">To:</span>
                      <input
                        type="date"
                        value={summaryDateTo}
                        onChange={(e) => setSummaryDateTo(e.target.value)}
                        className="px-2 py-1 bg-white border border-gray-200 rounded text-xs text-gray-800 focus:outline-none focus:border-gray-400"
                      />
                    </div>
                  )}

                  {/* Status Filter */}
                  <select
                    value={summaryStatusFilter}
                    onChange={(e) => setSummaryStatusFilter(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-xs sm:text-sm bg-white font-medium focus:ring-1 focus:ring-gray-400 focus:outline-none"
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="dispute">Service Dispute</option>
                  </select>

                  <button
                    onClick={downloadSummaryCsv}
                    disabled={loadingSummary || !summaryData || Object.keys(summaryData).length === 0}
                    className="px-3.5 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-xs sm:text-sm font-semibold disabled:opacity-50 hover:bg-gray-50 transition"
                  >
                    Download CSV
                  </button>
                  <button 
                    onClick={fetchSummary}
                    disabled={loadingSummary}
                    className="px-3.5 py-1.5 bg-gray-900 text-white rounded-lg text-xs sm:text-sm font-semibold disabled:opacity-50 hover:bg-gray-800 transition"
                  >
                    {loadingSummary ? 'Generating...' : 'Refresh Summary'}
                  </button>
                </div>
              </div>

               {!summaryData && !loadingSummary && (
                 <div className="text-center py-10 text-gray-500">
                   Select dates and click "Refresh Summary" to generate report.
                 </div>
               )}

               {summaryData && (
                 <div className="overflow-x-auto">
                   <table className="w-full text-left border-collapse">
                     <thead>
                       <tr className="bg-gray-50">
                         <th className="px-4 py-3 text-xs font-bold text-gray-600 uppercase tracking-wider border-b">Department</th>
                         <th className="px-4 py-3 text-xs font-bold text-gray-600 uppercase tracking-wider border-b">Request Kind</th>
                         <th className="px-4 py-3 text-xs font-bold text-gray-600 uppercase tracking-wider border-b text-right">Count</th>
                       </tr>
                     </thead>
                    <tbody>
                      {Object.entries(summaryData).map(([dept, data]) => {
                        const activities = Object.entries(data.activities || {});
                        if (activities.length === 0) return null;
                        const othersKey = `${dept}::Others`;
                        const othersExpanded = !!expandedSummaryOthers[othersKey];
                        const othersRows = Object.entries(data.others_breakdown || {});
                        const extraRows = othersExpanded ? othersRows.length : 0;
                        const rowSpan = activities.length + extraRows;

                        return activities.map(([activity, count], index) => {
                          const isOthers = String(activity).toLowerCase() === 'others';
                          const showOthersRows = isOthers && othersExpanded && othersRows.length > 0;

                          return (
                            <React.Fragment key={`${dept}-${activity}`}>
                              <tr className="hover:bg-gray-50 transition-colors">
                                {index === 0 && (
                                  <td className="px-4 py-3 font-bold text-gray-900 border-b align-top" rowSpan={rowSpan}>
                                    {dept}
                                    <div className="text-[10px] font-normal text-gray-500 mt-1">Total: {data.total} requests</div>
                                  </td>
                                )}
                                <td className="px-4 py-2.5 text-sm text-gray-700 border-b">
                                  {isOthers ? (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setExpandedSummaryOthers((prev) => ({
                                          ...prev,
                                          [othersKey]: !prev[othersKey]
                                        }))
                                      }
                                      className="inline-flex items-center gap-2 text-sm font-semibold text-gray-800 hover:text-gray-900"
                                    >
                                      <span>{othersExpanded ? '▾' : '▸'}</span>
                                      Others
                                    </button>
                                  ) : (
                                    activity
                                  )}
                                </td>
                                <td className="px-4 py-2.5 text-sm font-semibold text-gray-900 border-b text-right">
                                  {count}
                                </td>
                              </tr>
                              {showOthersRows &&
                                othersRows.map(([detail, detailCount]) => (
                                  <tr key={`${dept}-others-${detail}`} className="bg-gray-50/70">
                                    <td className="px-8 py-2 text-xs text-gray-700 border-b">{detail}</td>
                                    <td className="px-4 py-2 text-xs font-semibold text-gray-900 border-b text-right">{detailCount}</td>
                                  </tr>
                                ))}
                            </React.Fragment>
                          );
                        });
                      })}
                       {Object.keys(summaryData).length === 0 && (
                         <tr>
                           <td colSpan="3" className="px-4 py-8 text-center text-gray-500 italic">No data found for selected period</td>
                         </tr>
                       )}
                     </tbody>
                   </table>
                 </div>
               )}
             </div>
           ) : (
             <>
              <div className="px-1 flex items-center justify-between gap-3">
                <p className="text-xs sm:text-sm text-gray-600 font-medium">
                  {selectedTabCount} request{selectedTabCount === 1 ? '' : 's'} in {selectedTabLabel}
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={downloadRequestsPdf}
                    disabled={isExportingPdf || !filteredRequests || filteredRequests.length === 0}
                    className="px-3 py-1.5 border border-teal-200 text-teal-800 bg-teal-50 hover:bg-teal-100 rounded-lg text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-1.5 transition-colors shadow-sm"
                    title="Download filtered service requests as formatted PDF"
                  >
                    <FiDownload className="w-3.5 h-3.5 text-teal-600" />
                    {isExportingPdf ? 'Exporting PDF...' : 'Download Service Requests PDF'}
                  </button>
                  <button
                    type="button"
                    onClick={downloadRequestsCsv}
                    disabled={!filteredRequests || filteredRequests.length === 0}
                    className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-xs font-semibold disabled:opacity-50 hover:bg-gray-50 inline-flex items-center gap-1.5 transition-colors"
                  >
                    Download Service Requests CSV
                  </button>
                </div>
               </div>
               {filteredRequests.length === 0 ? (
                 <div className="fleet-card text-center py-16 px-6">
                   <div className="mx-auto h-12 w-12 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center mb-3">
                     <FiClock className="h-6 w-6 text-gray-500" />
                   </div>
                   <p className="text-sm text-gray-700 font-semibold mb-1">No requests found</p>
                   <p className="text-xs text-gray-500">Requests will appear here when guests make service requests</p>
                 </div>
               ) : (
                 <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                   {filteredRequests.map((request) => {
                     const status = normalizeStatus(request.status);
                     const bucket = getRequestBucket(request);
                     const dispatchStatus = getDispatchStatusLabel(request);
                     const completionCheck = getStaffCompletionCheckLabel(request);
                     const serviceRating = request.service_rating || request.metadata?.service_rating;
                     const requestActivity = getRequestActivity(request);
                     const isDispute = bucket === 'dispute' || request.dispatch_status === 'guest_disputed' || request.metadata?.dispatch_status === 'guest_disputed' || request.status === 'dispute';

                     return (
                       <div
                         key={request.id}
                         className="fleet-card p-4 flex flex-col gap-3 cursor-pointer hover:shadow-md transition-shadow"
                         onClick={() => openRequestDetails(request)}
                       >
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-semibold text-gray-900">Room {request.room_no || 'N/A'}</p>
                              <p className="text-xs text-gray-600">{request.guest_name || 'Unknown Guest'}</p>
                            </div>
                            <div className="flex flex-col items-end gap-1.5 shrink-0">
                              <span className={`px-2 py-0.5 text-[10px] font-medium rounded-md tracking-wide ${getCategoryColor(requestActivity)}`}>
                                {requestActivity}
                              </span>
                              {(() => {
                                const visibleSeenBy = (Array.isArray(request.metadata?.staff_seen_by) ? request.metadata.staff_seen_by : []).filter(
                                  (s) => !hiddenStaffPhones.has(normalizeDigits(s.phone)) && !hiddenStaffNames.has(String(s.name || '').trim().toLowerCase())
                                );
                                if (visibleSeenBy.length === 0) return null;
                                return (
                                  <span
                                    className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100"
                                    title={`Seen by: ${visibleSeenBy.map((s) => `${formatStaffSeenName(s)} (${formatOptionalDateTime(s.seen_at)})`).join(', ')}`}
                                  >
                                    <FiEye className="h-3 w-3 text-blue-600" />
                                    ({visibleSeenBy.length})
                                  </span>
                                );
                              })()}
                            </div>
                          </div>

                          <div className="space-y-1">
                            <p className="text-xs text-gray-500 font-mono">ID: {getRequestDisplayId(request)}</p>
                            <p className="text-sm text-gray-800 line-clamp-3 leading-snug">
                              {renderFormattedText(request.details) || 'No details provided'}
                            </p>
                          </div>

                          <div className="flex flex-wrap gap-1">
                            {request.metadata?.emergency_type && (
                              <span className="px-2 py-1 text-[11px] font-semibold rounded-full bg-red-100 text-red-800">
                                {String(request.metadata.emergency_type).toLowerCase() === 'medical'
                                  ? 'Medical Emergency'
                                  : 'Security Emergency'}
                              </span>
                            )}
                          </div>

                          <div className="flex items-end justify-between gap-2 mt-0.5">
                            <p className="text-[11px] text-gray-500 line-clamp-2">
                              {request.metadata?.created_by_user_name
                                ? `Created manually by ${request.metadata.created_by_user_name}`
                                : `Created by ${request.guest_name || 'Guest'}`}
                            </p>
                            <div className="text-right text-[11px] text-gray-500 shrink-0 leading-tight">
                              <div>{formatDate(request.created_at)}</div>
                              <div className="text-gray-400">{formatTime(request.created_at)}</div>
                            </div>
                          </div>

                         {serviceRating && <p className="text-xs text-gray-600">Rating: {'⭐'.repeat(serviceRating)} ({serviceRating}/5)</p>}

                         {bucket === 'pending' ? (
                            <div className="mt-auto" onClick={(e) => e.stopPropagation()}>
                              {!isSeenByCurrentUser(request) ? (
                                <div className="grid grid-cols-2 gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleMarkSeenFromDashboard(request)}
                                    disabled={markingSeenId === request.id}
                                    className="w-full px-2.5 py-2 rounded-lg text-xs font-semibold border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 active:scale-98 transition-all"
                                  >
                                    {markingSeenId === request.id ? 'Marking...' : 'Mark Seen'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => startCompletion(request)}
                                    className="w-full px-2.5 py-2 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-gray-800 active:scale-98 transition-all"
                                  >
                                    Complete
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => startCompletion(request)}
                                  className="w-full px-2.5 py-2 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-gray-800 active:scale-98 transition-all"
                                >
                                  Complete
                                </button>
                              )}
                            </div>
                         ) : bucket === 'cancelled' || request.status === 'cancelled' ? (
                           <div className="mt-auto text-xs text-rose-800 bg-rose-50/80 p-2.5 rounded-lg border border-rose-200/80">
                             <div className="font-bold flex items-center gap-1.5 text-rose-900">
                               <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-600"></span>
                               Cancelled
                             </div>
                             {request.metadata?.cancellation_reason && (
                               <p className="text-[11px] text-rose-700 line-clamp-2 mt-1">
                                 Reason: {request.metadata.cancellation_reason}
                               </p>
                             )}
                           </div>
                         ) : (
                           <div className="mt-auto flex flex-col gap-2">
                             <div className={`text-xs ${isDispute ? 'text-orange-800 font-semibold flex items-center gap-1.5' : getStaffFormOutcomeSummary(request).className}`}>
                               {isDispute ? (
                                 <>
                                   <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                                   Guest disputed completion
                                 </>
                               ) : (
                                 getStaffFormOutcomeSummary(request).text
                               )}
                             </div>

                             {isDispute && (
                               <div className="space-y-1.5 pt-0.5" onClick={(e) => e.stopPropagation()}>
                                 {request.metadata?.dispute_note && (
                                   <div className="p-2 rounded-lg bg-orange-50/90 border border-orange-200/90 text-xs">
                                     <div className="flex items-center justify-between text-[10px] text-orange-900 font-semibold mb-0.5">
                                       <span>Dispute Note</span>
                                       <span className="text-orange-700 font-normal truncate max-w-[120px]">
                                         {request.metadata?.dispute_note_by_user_name || 'Staff'}
                                       </span>
                                     </div>
                                     <p className="text-orange-950 text-[11px] line-clamp-2 italic">
                                       "{request.metadata.dispute_note}"
                                     </p>
                                   </div>
                                 )}
                                 <button
                                   type="button"
                                   onClick={() => openDisputeNoteModal(request)}
                                   className="w-full px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-orange-50 hover:bg-orange-100 active:scale-98 text-orange-800 border border-orange-300 transition-colors flex items-center justify-center gap-1.5 shadow-xs"
                                 >
                                   <FiEdit3 className="w-3.5 h-3.5 text-orange-600" />
                                   {request.metadata?.dispute_note ? 'Edit Dispute Note' : 'Write Note'}
                                 </button>
                               </div>
                             )}
                           </div>
                         )}
                       </div>
                     );
                   })}
                 </div>
               )}

               {/* Pagination UI */}
               {totalRequests > 0 && requestView !== 'summary' && (
                 <div className="flex items-center justify-between border-t border-gray-200 pt-4 mt-6">
                   <p className="text-sm text-gray-700">
                     Showing <span className="font-semibold">{(page - 1) * limit + 1}</span> to <span className="font-semibold">{Math.min(page * limit, totalRequests)}</span> of <span className="font-semibold">{totalRequests}</span> requests
                   </p>
                   <div className="flex items-center gap-2">
                     <button
                       onClick={() => setPage(Math.max(1, page - 1))}
                       disabled={page === 1}
                       className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${page === 1 ? 'border-gray-200 text-gray-400 bg-gray-50' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}
                     >
                       Previous
                     </button>
                     <button
                       onClick={() => setPage(page + 1)}
                       disabled={page * limit >= totalRequests}
                       className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${page * limit >= totalRequests ? 'border-gray-200 text-gray-400 bg-gray-50' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}
                     >
                       Next
                     </button>
                   </div>
                 </div>
               )}
             </>
           )}
         </div>

        {activeCompletionRequest && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
            <div
              className="w-full max-w-md bg-white rounded-2xl border border-gray-200 shadow-2xl"
              role="dialog"
              aria-modal="true"
              aria-labelledby="completion-modal-title"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <h3 id="completion-modal-title" className="text-base font-semibold text-gray-900">
                  Complete request
                </h3>
                <button
                  type="button"
                  onClick={cancelCompletion}
                  className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                  disabled={completionSavingId === activeCompletionRequest.id}
                >
                  <FiX className="h-4 w-4" />
                </button>
              </div>

              <div className="px-5 py-4 space-y-4">
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 space-y-1">
                  <p className="text-sm font-semibold text-gray-900">{activeCompletionRequest.guest_name || 'Guest'}</p>
                  <p className="text-xs text-gray-600">
                    Room <span className="font-semibold text-gray-800">{activeCompletionRequest.room_no || '—'}</span>
                  </p>
                  <p className="text-xs text-gray-600">
                    Activity:{' '}
                    <span className="font-semibold text-gray-800">
                      {getBookingActivityGuestLabel(activeCompletionRequest.category)}
                    </span>
                  </p>
                  {(activeCompletionRequest.metadata?.date_display || activeCompletionRequest.metadata?.booking_date) && (
                    <p className="text-xs text-gray-600">
                      Day:{' '}
                      <span className="font-semibold text-gray-800">
                        {activeCompletionRequest.metadata?.date_display || activeCompletionRequest.metadata?.booking_date}
                      </span>
                    </p>
                  )}
                  {(activeCompletionRequest.metadata?.time_display || activeCompletionRequest.metadata?.booking_time) && (
                    <p className="text-xs text-gray-600">
                      Slot:{' '}
                      <span className="font-semibold text-gray-800">
                        {activeCompletionRequest.metadata?.time_display || activeCompletionRequest.metadata?.booking_time}
                      </span>
                    </p>
                  )}
                  <p className="text-xs text-gray-500 pt-1 border-t border-gray-200 mt-2">
                    {activeCompletionRequest.details || 'No extra details'}
                  </p>
                </div>

                <div className="space-y-2">
                  <p id="completion-choice-heading" className="text-xs font-semibold text-gray-600">
                    Completed service request?
                  </p>
                  <div
                    className="flex flex-col gap-2"
                    role="radiogroup"
                    aria-labelledby="completion-choice-heading"
                  >
                    {COMPLETION_STATUS_OPTIONS.map((opt) => {
                      const selected = completionForm.completion_status === opt.value;
                      const saving = completionSavingId === activeCompletionRequest.id;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          disabled={saving}
                          onClick={() =>
                            setCompletionForm((f) => ({
                              ...f,
                              completion_status: opt.value
                            }))
                          }
                          className={[
                            'w-full rounded-xl border-2 px-4 py-3.5 text-left text-sm font-semibold transition-colors',
                            selected
                              ? 'border-gray-900 bg-gray-900 text-white shadow-sm'
                              : 'border-gray-200 bg-white text-gray-800 hover:border-gray-400 hover:bg-gray-50 active:bg-gray-100',
                            saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                          ].join(' ')}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Work done by <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={completionForm.work_done_by}
                    onChange={(e) => setCompletionForm((f) => ({ ...f, work_done_by: e.target.value }))}
                    disabled={completionSavingId === activeCompletionRequest.id}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gray-400 focus:outline-none"
                    placeholder="Enter staff name or team (required)"
                    required
                  />
                  {!completionForm.work_done_by?.trim() && (
                    <p className="text-[11px] text-amber-600 mt-1">Please specify the staff name or team before submitting.</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Remarks (optional)</label>
                  <textarea
                    value={completionForm.remarks}
                    onChange={(e) => setCompletionForm((f) => ({ ...f, remarks: e.target.value }))}
                    disabled={completionSavingId === activeCompletionRequest.id}
                    rows={3}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white resize-y"
                    placeholder="Notes"
                  />
                </div>
              </div>

              <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={cancelCompletion}
                  disabled={completionSavingId === activeCompletionRequest.id}
                  className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-semibold cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => submitDashboardCompletion(activeCompletionRequest)}
                  disabled={
                    !completionForm.completion_status || 
                    !completionForm.work_done_by?.trim() || 
                    completionSavingId === activeCompletionRequest.id
                  }
                  className="px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {completionSavingId === activeCompletionRequest.id ? 'Saving...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeDetailsRequest && (
          <div className="fixed inset-0 z-50 bg-black/40 overflow-y-auto">
            <div className="min-h-full flex items-start sm:items-center justify-center p-4">
            <div className="w-full max-w-3xl bg-white rounded-2xl border border-gray-200 shadow-2xl max-h-[calc(100vh-2rem)] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Service Request Details</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Request ID: {getRequestDisplayId(activeDetailsRequest)}</p>
                </div>
                <button
                  onClick={closeRequestDetails}
                  className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                >
                  <FiX className="h-4 w-4" />
                </button>
              </div>

              <div className="px-5 py-4 overflow-y-auto space-y-5 flex-1 min-h-0">
                {activeDetailsRequest.status === 'cancelled' && (
                  <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-900 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-rose-200 text-rose-800">
                        REQUEST CANCELLED
                      </span>
                      {activeDetailsRequest.metadata?.cancelled_at && (
                        <span className="text-xs text-rose-700 font-medium">
                          {formatOptionalDateTime(activeDetailsRequest.metadata.cancelled_at)}
                        </span>
                      )}
                    </div>
                    {activeDetailsRequest.metadata?.cancelled_by_user_name && (
                      <p className="text-xs text-rose-800">
                        Cancelled by: <strong className="font-semibold">{activeDetailsRequest.metadata.cancelled_by_user_name}</strong>
                      </p>
                    )}
                    {activeDetailsRequest.metadata?.cancellation_reason && (
                      <div className="mt-1.5 pt-1.5 border-t border-rose-200 text-xs">
                        <span className="font-bold text-rose-900">Reason: </span>
                        <span className="text-rose-800 font-medium">{activeDetailsRequest.metadata.cancellation_reason}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="rounded-lg border border-gray-200 p-3">
                    <p className="text-xs text-gray-500">Guest</p>
                    <p className="font-semibold text-gray-900 mt-0.5">{activeDetailsRequest.guest_name || 'Unknown Guest'}</p>
                    <p className="text-xs text-gray-600 mt-1">Room: {activeDetailsRequest.room_no || 'N/A'}</p>
                    <p className="text-xs text-gray-600">Phone: {activeDetailsRequest.phone || 'N/A'}</p>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-3">
                    <p className="text-xs text-gray-500">Service</p>
                    <p className="font-semibold text-gray-900 mt-0.5">{getRequestActivity(activeDetailsRequest)}</p>
                    <p className="text-xs text-gray-600 mt-1">Status: {normalizeStatus(activeDetailsRequest.status)}</p>
                    <p className="text-xs text-gray-600">Dispatch: {activeDetailsRequest.dispatch_status || activeDetailsRequest.metadata?.dispatch_status || 'N/A'}</p>
                  </div>
                </div>

                {(activeDetailsRequest.completion_response ||
                  activeDetailsRequest.metadata?.completion_response ||
                  activeDetailsRequest.dispatch_status ||
                  activeDetailsRequest.metadata?.dispatch_status) && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-4 space-y-2">
                    <p className="text-xs font-bold uppercase tracking-wide text-gray-600">Staff completion &amp; dispatch</p>
                    <div className="flex flex-wrap gap-2">
                      {getStaffCompletionCheckLabel(activeDetailsRequest) && (
                        <span
                          className={`inline-flex px-3 py-1.5 text-xs font-semibold rounded-lg ${
                            getStaffCompletionCheckLabel(activeDetailsRequest).classes
                          }`}
                        >
                          {getStaffCompletionCheckLabel(activeDetailsRequest).text}
                        </span>
                      )}
                      {getDispatchStatusLabel(activeDetailsRequest) && getDispatchStatusLabel(activeDetailsRequest).text !== 'Escalated to HOD' && (
                        <span
                          className={`inline-flex px-3 py-1.5 text-xs font-semibold rounded-lg ${
                            getDispatchStatusLabel(activeDetailsRequest).classes
                          }`}
                        >
                          {getDispatchStatusLabel(activeDetailsRequest).text}
                        </span>
                      )}
                      {hasValidEscalation(activeDetailsRequest) && (
                        <span className="inline-flex px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-100 text-red-800">
                          Escalated to HOD
                        </span>
                      )}
                    </div>
                    {(activeDetailsRequest.completion_response || activeDetailsRequest.metadata?.completion_response) && (
                      <p className="text-xs text-gray-600">
                        Raw response:{' '}
                        <span className="font-mono font-semibold text-gray-900">
                          {activeDetailsRequest.completion_response || activeDetailsRequest.metadata?.completion_response}
                        </span>
                        {activeDetailsRequest.metadata?.completion_response_at && (
                          <span className="text-gray-500">
                            {' '}
                            · {formatOptionalDateTime(activeDetailsRequest.metadata.completion_response_at)}
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                )}

                {/* Service Dispute Banner & Dispute Note */}
                {(activeDetailsRequest.dispatch_status === 'guest_disputed' ||
                  activeDetailsRequest.metadata?.dispatch_status === 'guest_disputed' ||
                  activeDetailsRequest.status === 'dispute' ||
                  activeDetailsRequest.metadata?.dispute_note) && (
                  <div className="rounded-xl border border-orange-200 bg-orange-50/70 p-4 space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-orange-100 text-orange-900 border border-orange-300">
                          <FiAlertTriangle className="h-3.5 w-3.5 text-orange-600" />
                          Service Dispute
                        </span>
                        {activeDetailsRequest.metadata?.guest_disputed_at && (
                          <span className="text-xs text-orange-800">
                            Disputed at: {formatOptionalDateTime(activeDetailsRequest.metadata.guest_disputed_at)}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => openDisputeNoteModal(activeDetailsRequest)}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-orange-600 hover:bg-orange-700 active:scale-98 text-white transition-colors flex items-center gap-1.5 shadow-sm"
                      >
                        <FiEdit3 className="w-3.5 h-3.5" />
                        {activeDetailsRequest.metadata?.dispute_note ? 'Edit Dispute Note' : 'Write Dispute Note'}
                      </button>
                    </div>

                    {activeDetailsRequest.metadata?.dispute_note ? (
                      <div className="p-3.5 rounded-xl bg-white border border-orange-200/90 shadow-xs space-y-2.5">
                        <div className="flex items-center justify-between flex-wrap gap-2 text-xs">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-gray-500">Note written by:</span>
                            <span className="font-bold text-gray-900">
                              {activeDetailsRequest.metadata?.dispute_note_by_user_name || 'Staff'}
                            </span>
                            {activeDetailsRequest.metadata?.dispute_note_by_user_email && (
                              <span className="text-gray-500 font-mono text-[11px]">
                                ({activeDetailsRequest.metadata.dispute_note_by_user_email})
                              </span>
                            )}
                            {activeDetailsRequest.metadata?.dispute_note_by_role && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-orange-100 text-orange-800 border border-orange-200">
                                {activeDetailsRequest.metadata.dispute_note_by_role}
                              </span>
                            )}
                          </div>
                          {activeDetailsRequest.metadata?.dispute_note_at && (
                            <span className="text-gray-500 text-[11px]">
                              {formatOptionalDateTime(activeDetailsRequest.metadata.dispute_note_at)}
                            </span>
                          )}
                        </div>

                        <div className="pt-2 border-t border-gray-100">
                          <p className="text-[11px] font-bold text-orange-950 uppercase tracking-wide mb-1">Dispute Note:</p>
                          <div className="p-3 rounded-lg bg-orange-50/40 border border-orange-100 text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
                            {activeDetailsRequest.metadata.dispute_note}
                          </div>
                        </div>

                        {Array.isArray(activeDetailsRequest.metadata?.dispute_notes) && activeDetailsRequest.metadata.dispute_notes.length > 1 && (
                          <details className="text-xs text-gray-600 pt-2 border-t border-gray-100">
                            <summary className="cursor-pointer font-semibold text-orange-900 hover:text-orange-950">
                              Previous notes history ({activeDetailsRequest.metadata.dispute_notes.length} total)
                            </summary>
                            <div className="mt-2 space-y-2 pl-2 border-l-2 border-orange-300">
                              {activeDetailsRequest.metadata.dispute_notes.map((entry, idx) => (
                                <div key={entry.id || idx} className="p-2.5 rounded-lg bg-gray-50 border border-gray-200 text-xs">
                                  <div className="flex items-center justify-between text-[11px] text-gray-600 mb-1">
                                    <span className="font-semibold text-gray-900">
                                      {entry.author_name} {entry.author_email ? `(${entry.author_email})` : ''}
                                      {entry.author_role ? ` · ${entry.author_role}` : ''}
                                    </span>
                                    <span>{formatOptionalDateTime(entry.created_at)}</span>
                                  </div>
                                  <p className="text-gray-800 whitespace-pre-wrap">{entry.note}</p>
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    ) : (
                      <div className="p-3.5 rounded-xl bg-white border border-dashed border-orange-300 text-center">
                        <p className="text-xs text-orange-900 font-medium">No dispute note recorded yet.</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">Click "Write Dispute Note" above to record a note with your current login.</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500">Request Details</p>
                  <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{renderFormattedText(activeDetailsRequest.details) || 'No details provided'}</p>
                </div>

                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wide">Timeline</div>
                  <div className="divide-y divide-gray-100 text-sm">
                    <div className="px-3 py-2 flex items-center justify-between">
                      <span className="text-gray-600">
                        {activeDetailsRequest.metadata?.created_by_user_name
                          ? `Request created manually by ${activeDetailsRequest.metadata.created_by_user_name} at`
                          : `Request created by ${activeDetailsRequest.guests?.name || activeDetailsRequest.guest_name || 'guest'} at`}
                      </span>
                      <span className="text-gray-900 font-medium">{formatOptionalDateTime(activeDetailsRequest.created_at)}</span>
                    </div>
                    <div className="px-3 py-2 flex items-start justify-between gap-2">
                      <span className="text-gray-600 flex items-center gap-1.5 pt-0.5">
                        <FiEye className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                        Seen by
                      </span>
                      <div className="text-right">
                        {(() => {
                          const visibleSeenBy = (Array.isArray(activeDetailsRequest.metadata?.staff_seen_by) ? activeDetailsRequest.metadata.staff_seen_by : []).filter(
                            (s) => !hiddenStaffPhones.has(normalizeDigits(s.phone)) && !hiddenStaffNames.has(String(s.name || '').trim().toLowerCase())
                          );
                          return visibleSeenBy.length > 0 ? (
                            <div className="space-y-1">
                              {visibleSeenBy.map((s, idx) => (
                                <div key={idx} className="text-xs font-semibold text-blue-900">
                                  {formatStaffSeenName(s)}{' '}
                                  <span className="text-gray-400 font-normal">
                                    ({formatOptionalDateTime(s.seen_at)})
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400 italic">Not opened by staff yet</span>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="px-3 py-2 flex items-center justify-between">
                      <span className="text-gray-600">
                        Acknowledged by {activeDetailsRequest.metadata?.staff_acknowledged_by_name || activeDetailsRequest.metadata?.assigned_staff_name || 'staff'} at
                      </span>
                      <span className="text-gray-900 font-medium">{formatOptionalDateTime(activeDetailsRequest.metadata?.staff_acknowledged_at)}</span>
                    </div>
                    <div className="px-3 py-2 flex items-center justify-between">
                      <span className="text-gray-600">
                        {activeDetailsRequest.metadata?.completed_by_staff_name
                          ? `Completed by ${activeDetailsRequest.metadata.completed_by_staff_name} at`
                          : 'Req completed at'}
                      </span>
                      <span className="text-gray-900 font-medium">{formatOptionalDateTime(activeDetailsRequest.completed_at || activeDetailsRequest.metadata?.completed_at || activeDetailsRequest.metadata?.completed_by_staff_at)}</span>
                    </div>
                  </div>
                </div>

                {(activeDetailsRequest.metadata?.flow_work_done_by || activeDetailsRequest.metadata?.flow_remarks) && (
                  <div className="rounded-lg border border-gray-200 overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wide">Staff Form Response</div>
                    <div className="divide-y divide-gray-100 text-sm">
                      {activeDetailsRequest.metadata?.flow_work_done_by && (
                        <div className="px-3 py-2 flex items-center justify-between">
                          <span className="text-gray-600">Work Done By</span>
                          <span className="text-gray-900 font-medium">{activeDetailsRequest.metadata.flow_work_done_by}</span>
                        </div>
                      )}
                      {activeDetailsRequest.metadata?.flow_remarks && (
                        <div className="px-3 py-2 flex flex-col gap-1">
                          <span className="text-gray-600">Remarks</span>
                          <span className="text-gray-900 font-medium whitespace-pre-wrap">{activeDetailsRequest.metadata.flow_remarks}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm">
                  <div className="bg-gray-50 px-4 py-2.5 text-xs font-bold text-gray-700 uppercase tracking-wide border-b border-gray-200">
                    Staff Dispatch &amp; Escalation Targets
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
                    {/* Column 1: Staff to which request was dispatched */}
                    <div className="p-4 flex flex-col space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                          Staff Dispatched ({activeDetailsTargets.staffTargets.length})
                        </span>
                      </div>
                      {activeDetailsTargets.staffTargets.length === 0 ? (
                        <p className="text-xs text-gray-400 italic py-2">No staff dispatch logs found.</p>
                      ) : (
                        <div className="space-y-2">
                          {activeDetailsTargets.staffTargets.map((item, idx) => (
                            <div key={idx} className="p-2.5 rounded-lg border border-gray-100 bg-gray-50/70">
                              <div className="flex items-center justify-between gap-1.5 flex-wrap">
                                <span className="text-sm font-semibold text-gray-900 truncate">{item.name}</span>
                                {item.badgeText && (
                                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md ${item.badgeClass}`}>
                                    {item.badgeText}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 font-mono mt-0.5">{item.phone}</p>
                              {(() => {
                                const seenInfo = Array.isArray(activeDetailsRequest.metadata?.staff_seen_by)
                                  ? activeDetailsRequest.metadata.staff_seen_by.find(
                                      (s) => normalizeDigits(s.phone) === normalizeDigits(item.phone)
                                    )
                                  : null;
                                return seenInfo ? (
                                  <p className="text-[11px] text-blue-600 font-medium mt-1 flex items-center gap-1">
                                    <FiEye className="h-3 w-3" />
                                    Seen at: {formatOptionalDateTime(seenInfo.seen_at)}
                                  </p>
                                ) : null;
                              })()}
                            </div>
                          ))}
                        </div>
                      )}
                      {activeDetailsRequest.metadata?.completed_by_staff_name && (
                        <div className="mt-1 p-2.5 rounded-lg bg-green-50/70 border border-green-200/80 text-xs text-green-900 flex items-center justify-between">
                          <span>Completed by: <strong>{activeDetailsRequest.metadata.completed_by_staff_name}</strong></span>
                          {activeDetailsRequest.metadata?.completed_by_staff_at && (
                            <span className="text-green-700 text-[10px]">{formatOptionalDateTime(activeDetailsRequest.metadata.completed_by_staff_at)}</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Column 2: HODs to which escalation message was sent */}
                    <div className="p-4 flex flex-col space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                          HOD Escalation ({activeDetailsTargets.hodTargets.length})
                        </span>
                        {activeDetailsTargets.isEscalated ? (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-200">
                            Escalated
                          </span>
                        ) : (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            Not Escalated
                          </span>
                        )}
                      </div>
                      {activeDetailsTargets.hodTargets.length === 0 ? (
                        <p className="text-xs text-gray-400 italic py-2">No HOD escalation contacts assigned.</p>
                      ) : (
                        <div className="space-y-2">
                          {activeDetailsTargets.hodTargets.map((item, idx) => (
                            <div key={idx} className={`p-2.5 rounded-lg border ${activeDetailsTargets.isEscalated ? 'border-red-100 bg-red-50/50' : 'border-gray-100 bg-gray-50/70'}`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-sm font-semibold text-gray-900 truncate">{item.name}</span>
                                  <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">HOD</span>
                                </div>
                                {activeDetailsTargets.isEscalated ? (
                                  <span className="text-[10px] font-bold text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                                    Message Sent
                                  </span>
                                ) : (
                                  <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                    Target HOD
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 font-mono mt-0.5">{item.phone}</p>
                              {(() => {
                                const seenInfo = Array.isArray(activeDetailsRequest.metadata?.staff_seen_by)
                                  ? activeDetailsRequest.metadata.staff_seen_by.find(
                                      (s) => normalizeDigits(s.phone) === normalizeDigits(item.phone)
                                    )
                                  : null;
                                return seenInfo ? (
                                  <p className="text-[11px] text-blue-600 font-medium mt-1 flex items-center gap-1">
                                    <FiEye className="h-3 w-3" />
                                    Seen at: {formatOptionalDateTime(seenInfo.seen_at)}
                                  </p>
                                ) : null;
                              })()}
                              {activeDetailsTargets.isEscalated && activeDetailsTargets.escalatedAt && (
                                <p className="text-[11px] text-red-600 font-medium mt-1">Escalated at: {formatOptionalDateTime(activeDetailsTargets.escalatedAt)}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Column 3: Super HODs */}
                    <div className="p-4 flex flex-col space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                          Super HOD ({activeDetailsTargets.superHodTargets.length})
                        </span>
                        {activeDetailsTargets.isSuperEscalated ? (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 border border-purple-200">
                            Super Escalated
                          </span>
                        ) : (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            {activeDetailsTargets.superHodTargets.length > 0 ? 'Assigned' : 'None'}
                          </span>
                        )}
                      </div>
                      {activeDetailsTargets.superHodTargets.length === 0 ? (
                        <p className="text-xs text-gray-400 italic py-2">No Super HOD assigned.</p>
                      ) : (
                        <div className="space-y-2">
                          {activeDetailsTargets.superHodTargets.map((item, idx) => (
                            <div key={idx} className={`p-2.5 rounded-lg border ${activeDetailsTargets.isSuperEscalated ? 'border-purple-100 bg-purple-50/50' : 'border-gray-100 bg-gray-50/70'}`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-sm font-semibold text-gray-900 truncate">{item.name}</span>
                                  <span className="text-[10px] font-bold text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded">Super HOD</span>
                                </div>
                                {activeDetailsTargets.isSuperEscalated ? (
                                  <span className="text-[10px] font-bold text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded">
                                    Message Sent
                                  </span>
                                ) : (
                                  <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                    Target Super HOD
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 font-mono mt-0.5">{item.phone}</p>
                              {(() => {
                                const seenInfo = Array.isArray(activeDetailsRequest.metadata?.staff_seen_by)
                                  ? activeDetailsRequest.metadata.staff_seen_by.find(
                                      (s) => normalizeDigits(s.phone) === normalizeDigits(item.phone)
                                    )
                                  : null;
                                return seenInfo ? (
                                  <p className="text-[11px] text-blue-600 font-medium mt-1 flex items-center gap-1">
                                    <FiEye className="h-3 w-3" />
                                    Seen at: {formatOptionalDateTime(seenInfo.seen_at)}
                                  </p>
                                ) : null;
                              })()}
                              {activeDetailsTargets.isSuperEscalated && activeDetailsTargets.superEscalatedAt && (
                                <p className="text-[11px] text-purple-600 font-medium mt-1">Super Escalated at: {formatOptionalDateTime(activeDetailsTargets.superEscalatedAt)}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {activeDetailsRequest.metadata?.completion_image_url && (
                  <div className="rounded-lg border border-gray-200 overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
                      <span>📸</span> Completion Photo
                    </div>
                    <div className="p-3">
                      <img
                        src={activeDetailsRequest.metadata.completion_image_url}
                        alt="Completion proof"
                        className="w-full max-h-72 object-contain rounded-lg border border-gray-100 bg-gray-50"
                      />
                      {activeDetailsRequest.metadata?.completion_image_uploaded_at && (
                        <p className="text-xs text-gray-500 mt-2">
                          Uploaded: {formatOptionalDateTime(activeDetailsRequest.metadata.completion_image_uploaded_at)}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between">
                <div>
                  {canCancelRequests && activeDetailsRequest.status !== 'cancelled' && (
                    <button
                      onClick={() => {
                        setCancellingRequest(activeDetailsRequest);
                        setCancelReason('');
                        setCancelError('');
                      }}
                      className="px-4 py-2 bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5"
                    >
                      <FiX className="h-4 w-4 text-rose-600" />
                      Cancel Request
                    </button>
                  )}
                </div>
                <button
                  onClick={closeRequestDetails}
                  className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-semibold"
                >
                  Close
                </button>
              </div>
            </div>
            </div>
          </div>
        )}

        {/* Dispute Note Modal */}
        {disputeNoteModalRequest && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
            <div className="w-full max-w-lg bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-orange-50/70">
                <div className="flex items-center gap-2.5">
                  <div className="h-8 w-8 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center">
                    <FiEdit3 className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">
                      {disputeNoteModalRequest.metadata?.dispute_note ? 'Edit Dispute Note' : 'Write Dispute Note'}
                    </h3>
                    <p className="text-[11px] text-gray-500 font-mono">
                      ID: {getRequestDisplayId(disputeNoteModalRequest)} · Room: {disputeNoteModalRequest.room_no || 'N/A'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeDisputeNoteModal}
                  disabled={disputeNoteSubmitting}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                >
                  <FiX className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSaveDisputeNote} className="p-5 space-y-4">
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-between text-xs">
                  <span className="text-gray-500 font-medium">Logged in author:</span>
                  <span className="font-semibold text-gray-900 flex items-center gap-1.5">
                    <span>{user?.name || user?.email || 'Staff'}</span>
                    {user?.email && <span className="text-gray-500 font-mono text-[11px]">({user.email})</span>}
                    {user?.role && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-orange-100 text-orange-800 border border-orange-200">
                        {user.role}
                      </span>
                    )}
                  </span>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                    Dispute Note <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    rows={4}
                    value={disputeNoteText}
                    onChange={(e) => setDisputeNoteText(e.target.value)}
                    placeholder="Write a small note explaining what happened, investigation details, resolution with guest, etc..."
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition"
                    autoFocus
                  />
                </div>

                {disputeNoteError && (
                  <div className="p-2.5 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700 font-medium">
                    {disputeNoteError}
                  </div>
                )}

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={closeDisputeNoteModal}
                    disabled={disputeNoteSubmitting}
                    className="px-4 py-2 rounded-xl text-xs font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={disputeNoteSubmitting}
                    className="px-5 py-2 rounded-xl text-xs font-bold bg-orange-600 hover:bg-orange-700 active:scale-98 text-white shadow-sm disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {disputeNoteSubmitting ? 'Saving Note...' : 'Save Dispute Note'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {cancellingRequest && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
            <div className="w-full max-w-md bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-rose-50/60">
                <div className="flex items-center gap-2.5">
                  <div className="h-8 w-8 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center">
                    <FiAlertTriangle className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">Cancel Service Request</h3>
                    <p className="text-[11px] text-gray-500 font-mono">
                      ID: {getRequestDisplayId(cancellingRequest)} · Room: {cancellingRequest.room_no || 'N/A'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (!cancelSubmitting) {
                      setCancellingRequest(null);
                      setCancelReason('');
                      setCancelError('');
                    }
                  }}
                  disabled={cancelSubmitting}
                  className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                >
                  <FiX className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleConfirmCancel} className="p-5 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                    Cancellation Reason <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    required
                    rows={3}
                    value={cancelReason}
                    onChange={(e) => {
                      setCancelReason(e.target.value);
                      if (cancelError) setCancelError('');
                    }}
                    placeholder="e.g. Guest requested cancellation, duplicate request, guest checked out..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-colors"
                    autoFocus
                  />
                  {cancelError && (
                    <p className="text-xs text-rose-600 mt-1 font-medium">{cancelError}</p>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => {
                      setCancellingRequest(null);
                      setCancelReason('');
                      setCancelError('');
                    }}
                    disabled={cancelSubmitting}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-semibold transition-colors"
                  >
                    Keep Request
                  </button>
                  <button
                    type="submit"
                    disabled={cancelSubmitting || !cancelReason.trim()}
                    className="px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5 shadow-sm"
                  >
                    {cancelSubmitting ? 'Cancelling...' : 'Confirm Cancellation'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {!isActivityBookingDepartmentUser && manualModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
            <div className="w-full max-w-lg bg-white rounded-2xl border border-gray-200 shadow-2xl">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <h3 className="text-base font-semibold text-gray-900">Create Manual Request</h3>
                <button
                  onClick={closeManualModal}
                  className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                  disabled={manualSaving}
                >
                  <FiX className="h-4 w-4" />
                </button>
              </div>

              <div className="px-5 py-4 space-y-4">
                {/* 1st Line: Room Number (Required) */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Room Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={manualForm.guest_room_no}
                    onChange={(e) => setManualForm((prev) => ({ ...prev, guest_room_no: e.target.value }))}
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-colors ${
                      manualForm.guest_room_no.trim() && !checkRoomExistsInPresetsOrQr(manualForm.guest_room_no).isValid
                        ? 'border-amber-400 bg-amber-50/20'
                        : 'border-gray-300'
                    }`}
                    placeholder="e.g. 204 or 405"
                    disabled={manualSaving}
                    autoFocus
                  />
                  {manualForm.guest_room_no.trim() && (
                    <div className="mt-1.5">
                      {checkRoomExistsInPresetsOrQr(manualForm.guest_room_no).isValid ? (
                        <p className="text-xs text-emerald-600 flex items-center gap-1 font-medium">
                          <FiCheck className="w-3.5 h-3.5 shrink-0" />
                          Verified in {checkRoomExistsInPresetsOrQr(manualForm.guest_room_no).matchedSource}
                        </p>
                      ) : (
                        <p className="text-xs text-amber-600 flex items-center gap-1 font-medium">
                          <FiAlertTriangle className="w-3.5 h-3.5 shrink-0" />
                          This room number does not have a QR (confirmation required on submit)
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* 2nd Line: Guest Name & Phone Number (Both Optional) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Guest Name <span className="font-normal text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={manualForm.guest_name}
                      onChange={(e) => setManualForm((prev) => ({ ...prev, guest_name: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white"
                      placeholder="Enter guest name"
                      disabled={manualSaving}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Phone Number <span className="font-normal text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={manualForm.guest_phone}
                      onChange={(e) => setManualForm((prev) => ({ ...prev, guest_phone: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white"
                      placeholder="10-digit mobile number"
                      disabled={manualSaving}
                    />
                  </div>
                </div>

                {/* 3rd Line: Department / Activity */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Department / Activity <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={manualForm.activity}
                    onChange={(e) => {
                      const newAct = e.target.value;
                      const options = getServiceOptionsForActivity(newAct);
                      setManualForm((prev) => ({
                        ...prev,
                        activity: newAct,
                        service_option_id: options[0]?.id || 'others',
                        details: ''
                      }));
                    }}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white"
                    disabled={manualSaving}
                  >
                    <option value="">Select activity</option>
                    {manualActivityOptions.map((activity) => (
                      <option key={activity} value={activity}>
                        {activity}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 4th Line: Service Request Item (Fetched from Settings + "Others") */}
                {manualForm.activity && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Service Request <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={manualForm.service_option_id}
                      onChange={(e) => setManualForm((prev) => ({ ...prev, service_option_id: e.target.value, details: '' }))}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white"
                      disabled={manualSaving}
                    >
                      {getServiceOptionsForActivity(manualForm.activity).map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.title} {opt.description && opt.id !== 'others' ? `(${opt.description})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {(manualForm.activity === 'SPA Appointment' || manualForm.activity === 'Salon') && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Service Type (e.g. Deep Tissue, Haircut) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={manualForm.service_type}
                      onChange={(e) => setManualForm((prev) => ({ ...prev, service_type: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white"
                      placeholder={`Enter ${manualForm.activity === 'Salon' ? 'salon' : 'spa'} service name`}
                      disabled={manualSaving}
                    />
                  </div>
                )}

                {/* 5th Line: Follow-up / Extra details or "Others" custom typing */}
                {manualForm.activity && (() => {
                  const currentOptions = getServiceOptionsForActivity(manualForm.activity);
                  const selectedOption = currentOptions.find((o) => o.id === manualForm.service_option_id);
                  const isOthers = manualForm.service_option_id === 'others';

                  if (isOthers) {
                    return (
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                          Request Details <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={manualForm.details}
                          onChange={(e) => setManualForm((prev) => ({ ...prev, details: e.target.value }))}
                          rows={3}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white"
                          placeholder="Type custom request details..."
                          disabled={manualSaving}
                        />
                      </div>
                    );
                  }

                  if (selectedOption?.detail_prompt_enabled) {
                    return (
                      <div className="space-y-2">
                        <label className="block text-xs font-semibold text-gray-700">
                          Follow-up Specifications / Details
                        </label>
                        {selectedOption.detail_prompt_message && (
                          <div className="p-2.5 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800 whitespace-pre-line leading-relaxed">
                            ℹ️ {selectedOption.detail_prompt_message.replace(/\*/g, '')}
                          </div>
                        )}
                        <textarea
                          value={manualForm.details}
                          onChange={(e) => setManualForm((prev) => ({ ...prev, details: e.target.value }))}
                          rows={2}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white"
                          placeholder="e.g. 2-Towels, 1-Brush"
                          disabled={manualSaving}
                        />
                      </div>
                    );
                  }

                  return (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        Additional Notes <span className="font-normal text-gray-400">(optional)</span>
                      </label>
                      <textarea
                        value={manualForm.details}
                        onChange={(e) => setManualForm((prev) => ({ ...prev, details: e.target.value }))}
                        rows={2}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white"
                        placeholder="Optional notes or specifications"
                        disabled={manualSaving}
                      />
                    </div>
                  );
                })()}
              </div>

              <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  onClick={closeManualModal}
                  disabled={manualSaving}
                  className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={submitManualRequest}
                  disabled={manualSaving}
                  className="px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm font-semibold disabled:opacity-60"
                >
                  {manualSaving ? 'Creating...' : 'Create Request'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* In-software Room QR Verification Modal */}
        {showRoomWarningModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-150">
            <div className="w-full max-w-sm bg-white rounded-2xl border border-gray-100 shadow-2xl p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-3.5">
                <FiAlertTriangle className="w-6 h-6" />
              </div>
              <h4 className="text-base font-bold text-gray-900">Room Verification</h4>
              <p className="text-sm text-gray-600 mt-2">
                This room number <span className="font-semibold text-gray-900 bg-gray-100 px-1.5 py-0.5 rounded">"{manualForm.guest_room_no.trim()}"</span> does not have a QR.
              </p>
              <p className="text-sm font-semibold text-gray-800 mt-2">
                Are you sure you want to proceed?
              </p>

              <div className="mt-5 flex items-center justify-center gap-2.5">
                <button
                  type="button"
                  onClick={() => setShowRoomWarningModal(false)}
                  disabled={manualSaving}
                  className="flex-1 py-2.5 px-3 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium text-xs transition-colors"
                >
                  Change Room
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowRoomWarningModal(false);
                    executeCreateManualRequest();
                  }}
                  disabled={manualSaving}
                  className="flex-1 py-2.5 px-3 rounded-xl bg-gray-900 hover:bg-black text-white font-semibold text-xs transition-colors shadow-sm"
                >
                  {manualSaving ? 'Creating...' : 'Yes, Proceed'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default ServiceRequests;
